import { SETTINGS_VIEW } from '../contexts/UIContext';
import { SCOPE_DEVIANCE_STANDARD } from '../helpers/constant';
import { IApplicationException } from './exception.type';
import { NorthStarTarget, NorthStarTransfoProgress } from './north-star-target.type';
import { PersistentResultDTO } from './persistent-result.type';
import { IRecommendationNext, RecommendationStatus } from './recommendation-next.type';
import { IRoadmap } from './roadmap.type';
import { RoadmapAction } from './roadmapAction.type';
import { IUser } from './user.type';

export interface INode {
  label: string;
  key: string | number;
  children?: INode[];
  fullPath?: string;
  selected?: boolean;
  disabled?: boolean;
  level?: number;
  parent?: INode;
  opened?: boolean;
  category?: boolean;
}

export interface ISection {
  path: string;
  label: string;
  display: boolean;
  component: JSX.Element | undefined;
  isLink?: boolean;
  notification?: number;
  applyFilter?: boolean;
  displayNotification?: boolean;
  childrens?: ISection[];
  isFullScreen?: boolean;
  isNew?: boolean;
}

export interface Paginated<T> {
  items: T[];
  meta: IPagination;
}

// This is a type that represents a paginated list by nestjs-paginate.
export interface PaginatedNext<T> {
  data: T[];
  meta: {
    applicationCount: number;
    hostnameCount: number;
    currentPage: number;
    itemsPerPage: number;
    totalItems: number;
    totalPages: number;
    sortBy: Array<[string, 'ASC' | 'DESC']>;
  };
}

export type GlobalStatus = 'idle' | 'error' | 'maintenance';
export type WorkerStatus = 'idle' | 'active' | 'error';

export interface IStatus {
  global: GlobalStatus;
  worker: WorkerStatus;
  lastRecordDate: string;
  lastApptioRecordMonth: string;
}

export interface IReferenceValue {
  key: number | string;
  name: string;
  resultKey?: string;
  disabled?: boolean;
  desc?: string;
  componentRefType?: string;
}

export const ALLOWED_TRIBES = [
  'GCOO/CFT/RRR',
  'GCOO/RBS/PBI/LUX/MKT',
  'GCOO/RBS/PBI/LUX/PIL',
  'GCOO/ARC/LUX',
  'GCOO/RBS/DCC/DEV/VDF',
  'GCOO/RBS/ASR/CSS/VDF',
  'GBTO/LUX/TSU',
  'GCOO/ISR/GBI',
];
export type ManagedByOptions = 'GBIS-GBSU' | 'GBIS-Non GBSU' | 'Non GBIS';

export type FlagType =
  | 'success'
  | 'warning'
  | 'danger'
  | 'dark'
  | 'secondary'
  | 'light'
  | 'transparent'
  | 'white'
  | 'black'
  | 'primary';

export type ApplicationResultType = 'value' | 'rule' | 'presence' | 'percentage';
export type ApplicationResultValue = 'good' | 'medium' | 'bad' | 'n/a' | 'n/d' | string | boolean | number;

export interface IApplicationClaim extends IBaseEntity {
  justification: string;
  status: string;
  application: IBaseEntity;
}

export interface ResultGroupedByName {
  name: string;
  results: IApplicationFullResult[];
}

export interface ResultGroupedByVersion {
  version: string | undefined;
  groupedResultsByName: ResultGroupedByName[];
}

export interface ResultGroupedByComponentRef {
  componentRef: IApplicationComponentRef | null; // null in case we want to display only type column (obsolescence score case)
  groupedResultsByVersion: ResultGroupedByVersion[];
}

export interface IGroupedComponentFull {
  type: string;
  componentsAggrByType: ResultGroupedByComponentRef[];
}

export interface IGroupedTotalCostComponents {
  type: string;
  componentsAggrByType: {
    componentRef: IApplicationComponentRef;
    groupedResults: IGroupedTotalCostResult[];
  }[];
}

export interface IGroupedTotalCostComponent {
  type: string;
  componentsAggrByType: {
    componentRef: IApplicationComponentRef;
    components: (ITotalCostResults | undefined)[];
  }[];
}

export interface IApplicationTab {
  appName: string;
  appId: number;
}

export interface IBaseEntity {
  id: number;
  createdDate: string;
  lastUpdate: string;
  createdBy: string;
  lastUpdateBy: string;
}

export interface IPagination {
  totalItems: number;
  itemCount: number;
  itemsPerPage: number;
  totalPages: number;
  currentPage: number;
}

export interface IApplicationParentRef extends IBaseEntity {
  scopesRef: IApplicationScopeRef[];
  name: string;
  key: string;
  comment: string;
}

export interface IApplicationScopeRef extends IBaseEntity {
  componentsRef: IApplicationComponentRef[];
  key: string;
  name: string;
  comment: string;
  type: string | null;
}

export interface IApplicationRef extends IBaseEntity {
  comment: string;
  key: string;
  name: string;
}

export interface IApplicationConfidence extends IBaseEntity {
  value: number;
  comment: string;
}

export interface IApplicationSource extends IBaseEntity {
  name: string;
  link: string | null;
  comment: string | null;
}

export enum VersionRefStatus {
  DEPRECATED = 'deprecated',
  TO_BE_DEPRECATED = 'to-be-deprecated',
  UP_TO_DATE = 'up-to-date',
}

export enum ChartMode {
  FAVORITE = 'Favorites',
  EXCEPTION = 'Exceptions',
  RECOMMENDATION = 'Recommendations',
  ROADMAP = 'Roadmaps',
  APPLICATION = 'Applications',
  CERTIFICATION = 'Certifications',
  ERRORS = 'Errors',
}
export enum TypeMode {
  ACTUAL = 'Actual',
  PREV_YEAR = 'Prev year',
  FORECAST = 'Forecast',
}

export interface IApplicationVersionRef extends IBaseEntity {
  version: string;
  versionAlias: string | null;
  status: VersionRefStatus;
  description: string;
  parentRef: IApplicationParentRef;
  parents: IApplicationParent[];
  scopeRef: IApplicationScopeRef;
  scopes: IApplicationScope[];
  componentRef: IApplicationComponentRef;
  components: IApplicationComponent[];
}
export interface IApplicationComponent extends IBaseEntity {
  results: IApplicationResult[];
  componentRef: IApplicationComponentRef;
  versionRef: IApplicationVersionRef | undefined;
  recommendations: IRecommendationNext[];
  scope: IApplicationScope | undefined;
}

export type ComponentRefVersionStatus = 'deprecated' | 'to_be_deprecated' | 'up_to_date';

export interface IApplicationParent extends IBaseEntity {
  parentRef: IApplicationRef;
  scopes: IApplicationScope[];
}

export interface IApplicationRecord extends IBaseEntity {
  name: string;
  type: string;
  parents: IApplicationParent[];
  result: IApplicationResult;
  application: IApplication;
  applicationId: number;
}

export interface IApplication extends IBaseEntity {
  iAppliName: IApplicationResult;
  iAppliID: IApplicationResult;
  description: IApplicationResult | null;
  records: IApplicationRecord[];
  exceptions: IApplicationException[];
  roadmaps: IRoadmap[];
  tags: ITag[];
  users: IUser[];
  globalState: IApplicationResult;
}

export interface IApplicationPaginated extends IBaseEntity {
  iAppliNameId: number;
  iAppliIdid: number;
  globalStateId: number;
  descriptionId: number;
}
export interface IResultPaginated {
  application: IApplicationPaginated;
  results: IApplicationResult[];
}
export interface IApplicationsResultPaginated {
  items: IResultPaginated[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
}

export interface IFavorite extends IBaseEntity {
  application: IApplication;
  user: IUser;
}
export interface IApplicationsPayload {
  items: IApplication[];
  meta: IPagination;
  links: {
    first: string;
    previous: string;
    next: string;
    last: string;
  };
}

export interface IBaseEntity {
  id: number;
  createdDate: string;
  lastUpdate: string;
  createdBy: string;
  lastUpdateBy: string;
}

export interface IPagination {
  totalItems: number;
  itemCount: number;
  itemsPerPage: number;
  totalPages: number;
  currentPage: number;
}

export interface IApplicationParentRef extends IBaseEntity {
  scopesRef: IApplicationScopeRef[];
  name: string;
  key: string;
  comment: string;
}

export interface IApplicationScopeRef extends IBaseEntity {
  componentsRef: IApplicationComponentRef[];
  key: string;
  name: string;
  comment: string;
  type: string | null;
  resultKeys: string[];
}

export type ProfileType = 'Application manager' | 'CIO' | 'RC/Tribe manager' | 'Architect' | 'Other';

export interface IApplicationRef extends IBaseEntity {
  comment: string;
  key: string;
  name: string;
}

export interface IApplicationConfidence extends IBaseEntity {
  value: number;
  comment: string;
}

export interface IApplicationSource extends IBaseEntity {
  name: string;
  link: string | null;
  comment: string | null;
}

export interface IApplicationResult extends IBaseEntity {
  comment: string;
  isMainResult: boolean;
  key: string;
  type: string;
  value: string;
  name: string | null;
  confidence: IApplicationConfidence;
  sources: IApplicationSource[];
  tbs?: ITbsResult;
  chasingId: number | null;
  component?: IApplicationComponent;
  scope?: IApplicationScope;
}
export interface IInfrastructureComponent {
  type: string;
  components: { key: ResultKey; name: string; versions: Array<string> | null }[];
}

export interface IRemediationComponent {
  scopeKey: ResultKey;
  scopeName: string;
  components: { key: ResultKey; name: string; versions: Array<string> | null }[];
}

export interface IApplicationComponentRef extends IBaseEntity {
  type: string;
  name: string;
  key: string;
  comment: string;
  resultKeys: string[];
  scopeRefId: number;
}

export interface IApplicationScope extends IBaseEntity {
  results: IApplicationResult[];
  scopeRef: IApplicationRef;
  components: IApplicationComponent[];
  parent: IApplicationParent;
}

export interface IApplicationParent extends IBaseEntity {
  parentRef: IApplicationRef;
  scopes: IApplicationScope[];
  record: IApplicationRecord;
}

export interface IApplicationRecord extends IBaseEntity {
  name: string;
  type: string;
  parents: IApplicationParent[];
  result: IApplicationResult;
}

export interface IFavorite extends IBaseEntity {
  user: IUser;
}
export interface IApplicationsPayload {
  items: IApplication[];
  meta: IPagination;
  links: {
    first: string;
    previous: string;
    next: string;
    last: string;
  };
}

export interface ITag extends IBaseEntity {
  tagId: number;
  name: string;
  description: string;
  application: IApplication;
}

export interface IParentRefCreate {
  key: string;
  name: string;
  comment?: string;
}

export interface IComponentRefCreate {
  key: string;
  name: string;
  type?: string;
  comment?: string;
  scopeRefID: number;
}

export interface IScopeRefCreate {
  key: string;
  name: string;
  type?: string;
  comment?: string;
  parentRefID: number;
}

export type DASHBOARD_VIEW =
  | 'global_view'
  | 'certification_view'
  | 'infrastructure_view'
  | 'tco_view'
  | 'health_view'
  | 'sgmarkets_view'
  | ResultKey;

export type ApplicationScope =
  | 'global_taxon'
  | 'global_sgmarkets'
  | 'other_obsolescence'
  | 'other_health'
  | 'other_users'
  | 'other_middlewares'
  | 'other_languages'
  | 'digital-capability_web'
  | 'digital-capability_api'
  | 'digital-capability_security'
  | 'digital-capability_ci-cd'
  | 'digital-capability_monitoring'
  | 'tco_cost';

export type ApplicationComponent =
  | 'global_taxon_criticality'
  | 'global_taxon_rto'
  | 'global_taxon_rpo'
  | 'global_taxon_tribe'
  | 'global_taxon_trigram'
  | 'global_taxon_owned-by'
  | 'global_taxon_country'
  | 'global_taxon_application-type'
  | 'global_sgmarkets_sgm-service'
  | 'other_obsolescence_mssql'
  | 'other_obsolescence_oracle'
  | 'other_obsolescence_physical-server'
  | 'other_obsolescence_postgresql'
  | 'other_middlewares_web-middleware'
  | 'other_middlewares_messaging-middleware'
  | 'other_middlewares_tibco-rdv-middleware'
  | 'other_middlewares_autosys-middleware'
  | 'other_languages_jvm-language'
  | 'other_languages_dot-net-language'
  | 'other_languages_c-cpp-language'
  | 'other_languages_python-language'
  | 'other_languages_datascience-language'
  | 'other_languages_web-language'
  | 'digital-capability_web_sgwt'
  | 'digital-capability_api_sgm-api'
  | 'digital-capability_security_sg-connect'
  | 'digital-capability_security_sg-iam'
  | 'digital-capability_ci-cd_github'
  | 'digital-capability_ci-cd_jenkins'
  | 'digital-capability_ci-cd_teamcity'
  | 'digital-capability_ci-cd_nexus'
  | 'digital-capability_ci-cd_artifactory-cdp'
  | 'digital-capability_ci-cd_artifactory-dsp'
  | 'digital-capability_ci-cd_sonar'
  | 'digital-capability_ci-cd_xldeploy'
  | 'digital-capability_monitoring_alerting-alerting'
  | 'digital-capability_monitoring_alerting-multi-channel-alerting'
  | 'digital-capability_monitoring_api-api-monitoring'
  | 'digital-capability_monitoring_app-apm'
  | 'digital-capability_monitoring_app-elasticsearch'
  | 'digital-capability_monitoring_app-elastic-agent-aas'
  | 'digital-capability_monitoring_app-grafana-aas'
  | 'digital-capability_monitoring_app-heartbeat-aas'
  | 'digital-capability_monitoring_app-logstash'
  | 'digital-capability_monitoring_app-metricbeat-aas'
  | 'digital-capability_monitoring_app-open-telemetry-aas'
  | 'digital-capability_monitoring_security-secure-log-platform'
  | 'digital-capability_monitoring_security-vault'
  | 'digital-capability_monitoring_web-web-monitoring'
  | 'tco_cost_gts'
  | 'tco_cost_azure'
  | 'tco_cost_workforce'
  | 'tco_cost_non-workforce'
  | 'tco_cost_software';

export interface ITbsResult {
  [key: string]: { [key: string]: number };
}

export const PARENT_RESULT_KEYS = ['cost', 'cost-prev-year', 'cost-forecast'] as const;

export const SCOPE_RESULT_KEYS = [
  'pops-users',
  'digital-capability-api',
  'digital-capability-security',
  'digital-capability-web',
  'sgmarkets',
  'obsolescence',
  'tbs',
  'finops',
  'digital-capability-monitoring',
  'health',
  'health-changes',
  'max-users',
  'digital-capability-ci-cd',
  'gbsu-cost',
  'gbsu-cost-forecast',
  'gbsu-cost-prev-year',
  'vendor-cost',
  'vendor-cost-forecast',
  'vendor-cost-prev-year',
  'infrastructure-cost',
  'infrastructure-cost-forecast',
  'infrastructure-cost-prev-year',
  //'gbis-standard',
  SCOPE_DEVIANCE_STANDARD,
] as const;

export const COMPONENT_RESULT_KEYS = [
  'digital-capability_api_api-pub',
  'digital-capability_api_average-api-data-quality',
  'digital-capability_api_average-api-functional-quality',
  'digital-capability_api_average-api-qos-quality',
  'digital-capability_api_average-api-security-quality',
  'digital-capability_api_average-api-technical-quality',
  'digital-capability_api_sgm-api',
  'digital-capability_ci-cd_github',
  'digital-capability_ci-cd_jenkins',
  'digital-capability_ci-cd_teamcity',
  'digital-capability_ci-cd_nexus',
  'digital-capability_ci-cd_artifactory-cdp',
  'digital-capability_ci-cd_artifactory-dsp',
  'digital-capability_ci-cd_sonar',
  'digital-capability_ci-cd_xldeploy',
  'digital-capability_monitoring_api-monitoring',
  'digital-capability_monitoring_architecture-type',
  'digital-capability_monitoring_global-monitoring',
  'digital-capability_monitoring_web-monitoring',
  'digital-capability_security_criticality',
  'digital-capability_security_internet-exposition',
  'digital-capability_security_pentest',
  'digital-capability_security_dataset',
  'digital-capability_security_sg-connect',
  'digital-capability_security_sg-iam',
  'digital-capability_security_tsa',
  'digital-capability_security_nist',
  'digital-capability_web_sg-bootstrap',
  'digital-capability_web_sgwt-widgets',
  'digital-capability_web_sgwt-widgets-account-center',
  'digital-capability_web_sgwt-widgets-help-center',
  'digital-capability_web_sgwt-widgets-mini-footer',
  'digital-capability_web_sgwt-widgets-splash-screen',
  'global_sgmarkets_sgm-service',
  'global_taxon_amer-managed',
  'global_taxon_application-type',
  'global_taxon_iappliname',
  'global_taxon_kearid',
  'global_taxon_iappliid',
  'global_taxon_application-manager-email',
  'global_taxon_irt',
  'global_taxon_bdr',
  'global_taxon_languages',
  'global_taxon_application-owner-email',
  'global_taxon_architecture-type',
  'global_taxon_country',
  'global_taxon_criticality',
  'global_taxon_rto',
  'global_taxon_rpo',
  'global_taxon_functional-architecture',
  'global_taxon_managed-by',
  'global_taxon_nova-compatible',
  'global_taxon_status',
  'global_taxon_tribe',
  'global_taxon_trigram',
  'global_taxon_owned-by',
  'global_taxon_ownership-rc',
  'global_taxon_management-rc',
  'global_taxon_application-decom-date',
  'global_taxon_kat-certification-date',
  'global_taxon_kat-certification-status',
  'global_taxon_kat-deployment-type',
  'tco_finops_orchestrated-container',
  'other_transformation-path_transformation-path',
  'null_null_tbs-enhancement-duplicate',
  'null_null_tbs-project-duplicate',
  'null_null_tbs-support-duplicate',
  'other_gbis-standard_abap',
  'other_gbis-standard_abcpdf-dot-net',
  'other_gbis-standard_action-script',
  'other_gbis-standard_ada',
  'other_gbis-standard_adaptive-server-entreprise-ado-dot-net-data-provider',
  'other_gbis-standard_ado-dot-net',
  'other_gbis-standard_ado-dot-net-entity-framework',
  'other_gbis-standard_ado-dot-net-entity-framework-tools',
  'other_gbis-standard_ags-script',
  'other_gbis-standard_al',
  'other_gbis-standard_alloy',
  'other_gbis-standard_ampl',
  'other_gbis-standard_angel-script',
  'other_gbis-standard_antlr',
  'other_gbis-standard_apache-conf',
  'other_gbis-standard_apache-web-server',
  'other_gbis-standard_apex',
  'other_gbis-standard_api-blueprint',
  'other_gbis-standard_apl',
  'other_gbis-standard_app-dynamics-dot-net-agent',
  'other_gbis-standard_apple-script',
  'other_gbis-standard_arc',
  'other_gbis-standard_asl',
  'other_gbis-standard_asp',
  'other_gbis-standard_asp-dot-net',
  'other_gbis-standard_asp-dot-net-ajax-extensions',
  'other_gbis-standard_asp-dot-net-core-hosting-bundle',
  'other_gbis-standard_asp-dot-net-core-runtime',
  'other_gbis-standard_asp-dot-net-core-runtime-package-store',
  'other_gbis-standard_asp-dot-net-core-runtime-package-store-pre-releases',
  'other_gbis-standard_asp-dot-net-core-shared-framework',
  'other_gbis-standard_asp-dot-net-core-shared-framework-pre-releases',
  'other_gbis-standard_asp-dot-net-frameworkd-and-tools-for-visual-studio-2013',
  'other_gbis-standard_asp-dot-net-lang',
  'other_gbis-standard_asp-dot-net-mvc',
  'other_gbis-standard_asp-dot-net-web-frameworks-and-tools-for-visual-studio-2015',
  'other_gbis-standard_aspect-j',
  'other_gbis-standard_assembly',
  'other_gbis-standard_auto-hoy-key',
  'other_gbis-standard_auto-it',
  'other_gbis-standard_autosys-middleware',
  'other_gbis-standard_awk',
  'other_gbis-standard_basic',
  'other_gbis-standard_batchfile',
  'other_gbis-standard_bicep',
  'other_gbis-standard_bikeshed',
  'other_gbis-standard_blade',
  'other_gbis-standard_blend-for-visual-studio-sdk-for-dot-net',
  'other_gbis-standard_boo',
  'other_gbis-standard_brighscript',
  'other_gbis-standard_brighter-script',
  'other_gbis-standard_businessobjects',
  'other_gbis-standard_business-works',
  'other_gbis-standard_c',
  'other_gbis-standard_cap-n-proto',
  'other_gbis-standard_carto-css',
  'other_gbis-standard_chart-controls-for-microsoft-dot-net-framework',
  'other_gbis-standard_clarion',
  'other_gbis-standard_classic-asp',
  'other_gbis-standard_clean',
  'other_gbis-standard_clojure',
  'other_gbis-standard_cmake',
  'other_gbis-standard_cobol',
  'other_gbis-standard_code-contracts-for-dot-net',
  'other_gbis-standard_coffee-script',
  'other_gbis-standard_cold-fusion',
  'other_gbis-standard_common-lisp',
  'other_gbis-standard_component-art-ui-framework-for-dot-net',
  'other_gbis-standard_connect-express-for-unix',
  'other_gbis-standard_cool',
  'other_gbis-standard_coq',
  'other_gbis-standard_cpp',
  'other_gbis-standard_cruise-control-dot-net',
  'other_gbis-standard_crystal-reports-for-dot-net-framework',
  'other_gbis-standard_crystal-reports-runtime-engine-for-dot-net-framework',
  'other_gbis-standard_c-sharp',
  'other_gbis-standard_csoud-document',
  'other_gbis-standard_csound-score',
  'other_gbis-standard_css',
  'other_gbis-standard_cuda',
  'other_gbis-standard_cython',
  'other_gbis-standard_d',
  'other_gbis-standard_dart',
  'other_gbis-standard_data-provider-for-dot-net',
  'other_gbis-standard_developer-express-dot-net',
  'other_gbis-standard_digital-command-language',
  'other_gbis-standard_dockerfile',
  'other_gbis-standard_documentum',
  'other_gbis-standard_dot-net-compact-framework',
  'other_gbis-standard_dot-net-core-host-pre-releases',
  'other_gbis-standard_dot-net-core-runtime',
  'other_gbis-standard_dot-net-core-runtime-pre-release',
  'other_gbis-standard_dot-net-core-runtime-pre-releases',
  'other_gbis-standard_dot-net-core-tools-visual-studio',
  'other_gbis-standard_dot-net-core-windows-server-hosting',
  'other_gbis-standard_dot-net-framework',
  'other_gbis-standard_dot-net-framework-client-profile',
  'other_gbis-standard_dot-net-framework-extended',
  'other_gbis-standard_dot-net-framework-sdk',
  'other_gbis-standard_dot-net-memory-profiler',
  'other_gbis-standard_dot-net-reflector',
  'other_gbis-standard_dot-net-runtime',
  'other_gbis-standard_dot-net-sdk',
  'other_gbis-standard_dot-net-sdk-pre-releases',
  'other_gbis-standard_dtrace',
  'other_gbis-standard_eagle',
  'other_gbis-standard_ec',
  'other_gbis-standard_ecl',
  'other_gbis-standard_eiffel',
  'other_gbis-standard_ejs',
  'other_gbis-standard_elixir',
  'other_gbis-standard_elm',
  'other_gbis-standard_emacs-lisp',
  'other_gbis-standard_ems',
  'other_gbis-standard_eq',
  'other_gbis-standard_erlang',
  'other_gbis-standard_euphoria',
  'other_gbis-standard_extrem-application-platform-xap',
  'other_gbis-standard_faust',
  'other_gbis-standard_fluent',
  'other_gbis-standard_flux',
  'other_gbis-standard_forth',
  'other_gbis-standard_fortran',
  'other_gbis-standard_free-basic',
  'other_gbis-standard_free-marker',
  'other_gbis-standard_f-sharp',
  'other_gbis-standard_f-star',
  'other_gbis-standard_futhark',
  'other_gbis-standard_game-maker-language',
  'other_gbis-standard_gap',
  'other_gbis-standard_gcc-machine-description',
  'other_gbis-standard_gdb',
  'other_gbis-standard_gemfire',
  'other_gbis-standard_genero-forms',
  'other_gbis-standard_genshi',
  'other_gbis-standard_gherkin',
  'other_gbis-standard_giga-spaces-xap-dot-net-12-2-1',
  'other_gbis-standard_giga-spaces-xap-dot-net-16-1-0',
  'other_gbis-standard_giga-spaces-xap-dot-net-premium-edition-9-7-2-x86',
  'other_gbis-standard_glsl',
  'other_gbis-standard_gnuplot',
  'other_gbis-standard_go',
  'other_gbis-standard_grammatical-framework',
  'other_gbis-standard_groovy',
  'other_gbis-standard_hack',
  'other_gbis-standard_handlebars',
  'other_gbis-standard_harbour',
  'other_gbis-standard_haskell',
  'other_gbis-standard_hcl',
  'other_gbis-standard_hd-insight-azure',
  'other_gbis-standard_hive-ql',
  'other_gbis-standard_hlsl',
  'other_gbis-standard_html',
  'other_gbis-standard_idl',
  'other_gbis-standard_igor-pro',
  'other_gbis-standard_inno-setup',
  'other_gbis-standard_io',
  'other_gbis-standard_iprocess',
  'other_gbis-standard_iron-python-for-dot-net-4',
  'other_gbis-standard_jasmin',
  'other_gbis-standard_java',
  'other_gbis-standard_java-development-kit',
  'other_gbis-standard_java-platform',
  'other_gbis-standard_java-runtime-environement',
  'other_gbis-standard_javascript',
  'other_gbis-standard_jboss-eap',
  'other_gbis-standard_jboss-wildfly',
  'other_gbis-standard_jinja',
  'other_gbis-standard_jolie',
  'other_gbis-standard_jq',
  'other_gbis-standard_jsonnet',
  'other_gbis-standard_jupyter-notebook',
  'other_gbis-standard_kotlin',
  'other_gbis-standard_kubernetes-cloud-azure',
  'other_gbis-standard_lasso',
  'other_gbis-standard_less',
  'other_gbis-standard_lex',
  'other_gbis-standard_limbo',
  'other_gbis-standard_linux',
  'other_gbis-standard_liquid',
  'other_gbis-standard_llvm',
  'other_gbis-standard_logos',
  'other_gbis-standard_lua',
  'other_gbis-standard_m',
  'other_gbis-standard_m4',
  'other_gbis-standard_makefile',
  'other_gbis-standard_mako',
  'other_gbis-standard_mask',
  'other_gbis-standard_mathematica',
  'other_gbis-standard_matlab',
  'other_gbis-standard_max',
  'other_gbis-standard_mercury',
  'other_gbis-standard_meson',
  'other_gbis-standard_messaging-middleware',
  'other_gbis-standard_mirc-script',
  'other_gbis-standard_modelica',
  'other_gbis-standard_module-management-system',
  'other_gbis-standard_moocode',
  'other_gbis-standard_moon-script',
  'other_gbis-standard_mq',
  'other_gbis-standard_mssql',
  'other_gbis-standard_mustache',
  'other_gbis-standard_myghty',
  'other_gbis-standard_nasl',
  'other_gbis-standard_ncl',
  'other_gbis-standard_new-lisp',
  'other_gbis-standard_nextflow',
  'other_gbis-standard_nginx',
  'other_gbis-standard_nix',
  'other_gbis-standard_nsis',
  'other_gbis-standard_nu',
  'other_gbis-standard_objective-c',
  'other_gbis-standard_objective-cpp',
  'other_gbis-standard_objective-j',
  'other_gbis-standard_ocaml',
  'other_gbis-standard_opa',
  'other_gbis-standard_open-edge-abl',
  'other_gbis-standard_open-java-development-kit',
  'other_gbis-standard_open-jdk-runtime-environement',
  'other_gbis-standard_open-policy-agent',
  'other_gbis-standard_oracle',
  'other_gbis-standard_part-cover-dot-net',
  'other_gbis-standard_pascal',
  'other_gbis-standard_pawn',
  'other_gbis-standard_peoplesoft',
  'other_gbis-standard_perl',
  'other_gbis-standard_perl-6',
  'other_gbis-standard_pgsql-azure',
  'other_gbis-standard_php',
  'other_gbis-standard_physical-server',
  'other_gbis-standard_pig-latin',
  'other_gbis-standard_pl-pgsql',
  'other_gbis-standard_pl-sql',
  'other_gbis-standard_postgresql',
  'other_gbis-standard_post-script',
  'other_gbis-standard_pov-ray-sdl',
  'other_gbis-standard_power-bi-report-server',
  'other_gbis-standard_power-builder',
  'other_gbis-standard_powercenter',
  'other_gbis-standard_power-shell',
  'other_gbis-standard_prolog',
  'other_gbis-standard_protocol-buffer',
  'other_gbis-standard_pug',
  'other_gbis-standard_puppet',
  'other_gbis-standard_pure-basic',
  'other_gbis-standard_pure-data',
  'other_gbis-standard_python',
  'other_gbis-standard_q',
  'other_gbis-standard_qmake',
  'other_gbis-standard_qml',
  'other_gbis-standard_qt-script',
  'other_gbis-standard_r',
  'other_gbis-standard_rabbit-mq',
  'other_gbis-standard_raku',
  'other_gbis-standard_rebol',
  'other_gbis-standard_redcode',
  'other_gbis-standard_rescript',
  'other_gbis-standard_rexx',
  'other_gbis-standard_rich-text-format',
  'other_gbis-standard_robot-framework',
  'other_gbis-standard_roff',
  'other_gbis-standard_rpc',
  'other_gbis-standard_ruby',
  'other_gbis-standard_rust',
  'other_gbis-standard_sas',
  'other_gbis-standard_sass',
  'other_gbis-standard_scala',
  'other_gbis-standard_scheme',
  'other_gbis-standard_scilab',
  'other_gbis-standard_scss',
  'other_gbis-standard_sed',
  'other_gbis-standard_server-gen',
  'other_transformation-path_server-gen-one',
  'other_transformation-path_server-gen-two',
  'other_transformation-path_server-tradi',
  'other_transformation-path_server-azure',
  'other_transformation-path_server-aws',
  'other_gbis-standard_shell',
  'other_gbis-standard_slice',
  'other_gbis-standard_smalltalk',
  'other_gbis-standard_smarty',
  'other_gbis-standard_smpl',
  'other_gbis-standard_source-pawn',
  'other_gbis-standard_sonar',
  'other_gbis-standard_sql',
  'other_gbis-standard_sqlpl',
  'other_gbis-standard_sql-server-analysis-services',
  'other_gbis-standard_sql-server-integration-services',
  'other_gbis-standard_sql-server-reporting-services',
  'other_gbis-standard_srecode-template',
  'other_gbis-standard_standard-ml',
  'other_gbis-standard_starlark',
  'other_gbis-standard_stata',
  'other_gbis-standard_sterling-file-gateway',
  'other_gbis-standard_string-template',
  'other_gbis-standard_stylus',
  'other_gbis-standard_svelte',
  'other_gbis-standard_swift',
  'other_gbis-standard_swig',
  'other_gbis-standard_synchronization-services-for-ado-dot-net',
  'other_gbis-standard_system-verilog',
  'other_gbis-standard_talend-bigdata',
  'other_gbis-standard_tcl',
  'other_gbis-standard_tex',
  'other_gbis-standard_thrift',
  'other_gbis-standard_tibco-rdv-middleware',
  'other_gbis-standard_tla',
  'other_gbis-standard_tomcat',
  'other_gbis-standard_tsa',
  'other_gbis-standard_tsql',
  'other_gbis-standard_tuxedo-dot-net-client',
  'other_gbis-standard_twig',
  'other_gbis-standard_typescript',
  'other_gbis-standard_ui-for-asp-dot-net-mvc',
  'other_gbis-standard_vba',
  'other_gbis-standard_vb-script',
  'other_gbis-standard_vim-script',
  'other_gbis-standard_vim-snippet',
  'other_gbis-standard_vision-for-dot-net',
  'other_gbis-standard_visual-basic',
  'other_gbis-standard_visual-basic-6',
  'other_gbis-standard_visual-basic-dot-net',
  'other_gbis-standard_visual-studio-ado-dot-net-entity-framework-tools',
  'other_gbis-standard_vue',
  'other_gbis-standard_web-assembly',
  'other_gbis-standard_weblogic-server',
  'other_gbis-standard_webmethods',
  'other_gbis-standard_web-middleware',
  'other_gbis-standard_windows',
  'other_gbis-standard_windows-azure-libraries-for-dot-net',
  'other_gbis-standard_xap-dot-net',
  'other_gbis-standard_x-base',
  'other_gbis-standard_x-proc',
  'other_gbis-standard_x-query',
  'other_gbis-standard_xs',
  'other_gbis-standard_xslt',
  'other_gbis-standard_xtend',
  'other_gbis-standard_yacc',
  'other_gbis-standard_zen-script',
  'other_gbis-standard_xonsh',
  'other_gbis-standard_filebench-wml',
  'other_gbis-standard_reason',
  'other_gbis-standard_slash',
  'other_gbis-standard_v',
  'other_gbis-standard_verilog',
  'other_health_changes',
  'other_health_emergency-changes',
  'other_health_incidents',
  'other_health_mim',
  'other_health_sev-0-incidents',
  'other_health_sev-1-incidents',
  'other_health_sev-2-incidents',
  'other_middlewares_autosys-middleware',
  'other_middlewares_messaging-middleware',
  'other_middlewares_tibco-rdv-middleware',
  'other_middlewares_web-middleware',
  'other_obsolescence_abap',
  'other_obsolescence_abcpdf-dot-net',
  'other_obsolescence_action-script',
  'other_obsolescence_ada',
  'other_obsolescence_adaptive-server-entreprise-ado-dot-net-data-provider',
  'other_obsolescence_ado-dot-net',
  'other_obsolescence_ado-dot-net-entity-framework',
  'other_obsolescence_ado-dot-net-entity-framework-tools',
  'other_obsolescence_ags-script',
  'other_obsolescence_al',
  'other_obsolescence_alloy',
  'other_obsolescence_ampl',
  'other_obsolescence_angel-script',
  'other_obsolescence_antlr',
  'other_obsolescence_apache-conf',
  'other_obsolescence_apache-web-server',
  'other_obsolescence_apex',
  'other_obsolescence_api-blueprint',
  'other_obsolescence_apl',
  'other_obsolescence_app-dynamics-dot-net-agent',
  'other_obsolescence_apple-script',
  'other_obsolescence_arc',
  'other_obsolescence_asl',
  'other_obsolescence_asp',
  'other_obsolescence_asp-dot-net',
  'other_obsolescence_asp-dot-net-ajax-extensions',
  'other_obsolescence_asp-dot-net-core-hosting-bundle',
  'other_obsolescence_asp-dot-net-core-runtime',
  'other_obsolescence_asp-dot-net-core-runtime-package-store',
  'other_obsolescence_asp-dot-net-core-runtime-package-store-pre-releases',
  'other_obsolescence_asp-dot-net-core-shared-framework',
  'other_obsolescence_asp-dot-net-core-shared-framework-pre-releases',
  'other_obsolescence_asp-dot-net-frameworkd-and-tools-for-visual-studio-2013',
  'other_obsolescence_asp-dot-net-lang',
  'other_obsolescence_asp-dot-net-mvc',
  'other_obsolescence_asp-dot-net-web-frameworks-and-tools-for-visual-studio-2015',
  'other_obsolescence_aspect-j',
  'other_obsolescence_assembly',
  'other_obsolescence_auto-hoy-key',
  'other_obsolescence_auto-it',
  'other_obsolescence_autosys-middleware',
  'other_obsolescence_awk',
  'other_obsolescence_basic',
  'other_obsolescence_batchfile',
  'other_obsolescence_bicep',
  'other_obsolescence_bikeshed',
  'other_obsolescence_blade',
  'other_obsolescence_blend-for-visual-studio-sdk-for-dot-net',
  'other_obsolescence_boo',
  'other_obsolescence_brighscript',
  'other_obsolescence_brighter-script',
  'other_obsolescence_businessobjects',
  'other_obsolescence_business-works',
  'other_obsolescence_c',
  'other_obsolescence_cap-n-proto',
  'other_obsolescence_carto-css',
  'other_obsolescence_chart-controls-for-microsoft-dot-net-framework',
  'other_obsolescence_clarion',
  'other_obsolescence_classic-asp',
  'other_obsolescence_clean',
  'other_obsolescence_clojure',
  'other_obsolescence_cmake',
  'other_obsolescence_cobol',
  'other_obsolescence_code-contracts-for-dot-net',
  'other_obsolescence_coffee-script',
  'other_obsolescence_cold-fusion',
  'other_obsolescence_common-lisp',
  'other_obsolescence_component-art-ui-framework-for-dot-net',
  'other_obsolescence_connect-express-for-unix',
  'other_obsolescence_cool',
  'other_obsolescence_coq',
  'other_obsolescence_cpp',
  'other_obsolescence_cruise-control-dot-net',
  'other_obsolescence_crystal-reports-for-dot-net-framework',
  'other_obsolescence_crystal-reports-runtime-engine-for-dot-net-framework',
  'other_obsolescence_c-sharp',
  'other_obsolescence_csoud-document',
  'other_obsolescence_csound-score',
  'other_obsolescence_css',
  'other_obsolescence_cuda',
  'other_obsolescence_cython',
  'other_obsolescence_d',
  'other_obsolescence_dart',
  'other_obsolescence_data-provider-for-dot-net',
  'other_obsolescence_developer-express-dot-net',
  'other_obsolescence_digital-command-language',
  'other_obsolescence_dockerfile',
  'other_obsolescence_documentum',
  'other_obsolescence_dot-net-compact-framework',
  'other_obsolescence_dot-net-core-host-pre-releases',
  'other_obsolescence_dot-net-core-runtime',
  'other_obsolescence_dot-net-core-runtime-pre-release',
  'other_obsolescence_dot-net-core-runtime-pre-releases',
  'other_obsolescence_dot-net-core-tools-visual-studio',
  'other_obsolescence_dot-net-core-windows-server-hosting',
  'other_obsolescence_dot-net-framework',
  'other_obsolescence_dot-net-framework-client-profile',
  'other_obsolescence_dot-net-framework-extended',
  'other_obsolescence_dot-net-framework-sdk',
  'other_obsolescence_dot-net-memory-profiler',
  'other_obsolescence_dot-net-reflector',
  'other_obsolescence_dot-net-runtime',
  'other_obsolescence_dot-net-sdk',
  'other_obsolescence_dot-net-sdk-pre-releases',
  'other_obsolescence_dtrace',
  'other_obsolescence_eagle',
  'other_obsolescence_ec',
  'other_obsolescence_ecl',
  'other_obsolescence_eiffel',
  'other_obsolescence_ejs',
  'other_obsolescence_elixir',
  'other_obsolescence_elm',
  'other_obsolescence_emacs-lisp',
  'other_obsolescence_ems',
  'other_obsolescence_eq',
  'other_obsolescence_erlang',
  'other_obsolescence_euphoria',
  'other_obsolescence_extrem-application-platform-xap',
  'other_obsolescence_faust',
  'other_obsolescence_filebench-wml',
  'other_obsolescence_fluent',
  'other_obsolescence_flux',
  'other_obsolescence_forth',
  'other_obsolescence_fortran',
  'other_obsolescence_free-basic',
  'other_obsolescence_free-marker',
  'other_obsolescence_f-sharp',
  'other_obsolescence_f-star',
  'other_obsolescence_futhark',
  'other_obsolescence_game-maker-language',
  'other_obsolescence_gap',
  'other_obsolescence_gcc-machine-description',
  'other_obsolescence_gdb',
  'other_obsolescence_gemfire',
  'other_obsolescence_genero-forms',
  'other_obsolescence_genshi',
  'other_obsolescence_gherkin',
  'other_obsolescence_giga-spaces-xap-dot-net-12-2-1',
  'other_obsolescence_giga-spaces-xap-dot-net-16-1-0',
  'other_obsolescence_giga-spaces-xap-dot-net-premium-edition-9-7-2-x86',
  'other_obsolescence_glsl',
  'other_obsolescence_gnuplot',
  'other_obsolescence_go',
  'other_obsolescence_grammatical-framework',
  'other_obsolescence_groovy',
  'other_obsolescence_hack',
  'other_obsolescence_handlebars',
  'other_obsolescence_harbour',
  'other_obsolescence_haskell',
  'other_obsolescence_hcl',
  'other_obsolescence_hd-insight-azure',
  'other_obsolescence_hive-ql',
  'other_obsolescence_hlsl',
  'other_obsolescence_html',
  'other_obsolescence_idl',
  'other_obsolescence_igor-pro',
  'other_obsolescence_inno-setup',
  'other_obsolescence_io',
  'other_obsolescence_iprocess',
  'other_obsolescence_iron-python-for-dot-net-4',
  'other_obsolescence_jasmin',
  'other_obsolescence_java',
  'other_obsolescence_java-development-kit',
  'other_obsolescence_java-platform',
  'other_obsolescence_java-runtime-environement',
  'other_obsolescence_javascript',
  'other_obsolescence_jboss-eap',
  'other_obsolescence_jboss-wildfly',
  'other_obsolescence_jinja',
  'other_obsolescence_jolie',
  'other_obsolescence_jq',
  'other_obsolescence_jsonnet',
  'other_obsolescence_jupyter-notebook',
  'other_obsolescence_kotlin',
  'other_obsolescence_sonar',
  'other_obsolescence_kubernetes',
  'other_obsolescence_kubernetes-cloud-azure',
  'other_obsolescence_lasso',
  'other_obsolescence_less',
  'other_obsolescence_lex',
  'other_obsolescence_limbo',
  'other_obsolescence_linux',
  'other_obsolescence_liquid',
  'other_obsolescence_llvm',
  'other_obsolescence_logos',
  'other_obsolescence_lua',
  'other_obsolescence_m',
  'other_obsolescence_m4',
  'other_obsolescence_makefile',
  'other_obsolescence_mako',
  'other_obsolescence_mask',
  'other_obsolescence_mathematica',
  'other_obsolescence_matlab',
  'other_obsolescence_max',
  'other_obsolescence_mercury',
  'other_obsolescence_meson',
  'other_obsolescence_messaging-middleware',
  'other_obsolescence_mirc-script',
  'other_obsolescence_modelica',
  'other_obsolescence_module-management-system',
  'other_obsolescence_moocode',
  'other_obsolescence_moon-script',
  'other_obsolescence_mq',
  'other_obsolescence_mssql',
  'other_obsolescence_mustache',
  'other_obsolescence_myghty',
  'other_obsolescence_nasl',
  'other_obsolescence_ncl',
  'other_obsolescence_new-lisp',
  'other_obsolescence_nextflow',
  'other_obsolescence_nginx',
  'other_obsolescence_nix',
  'other_obsolescence_nsis',
  'other_obsolescence_nu',
  'other_obsolescence_objective-c',
  'other_obsolescence_objective-cpp',
  'other_obsolescence_objective-j',
  'other_obsolescence_ocaml',
  'other_obsolescence_opa',
  'other_obsolescence_open-edge-abl',
  'other_obsolescence_open-java-development-kit',
  'other_obsolescence_open-jdk-runtime-environement',
  'other_obsolescence_open-policy-agent',
  'other_obsolescence_oracle',
  'other_obsolescence_part-cover-dot-net',
  'other_obsolescence_pascal',
  'other_obsolescence_pawn',
  'other_obsolescence_peoplesoft',
  'other_obsolescence_perl',
  'other_obsolescence_perl-6',
  'other_obsolescence_pgsql-azure',
  'other_obsolescence_php',
  'other_obsolescence_physical-server',
  'other_obsolescence_pig-latin',
  'other_obsolescence_pl-pgsql',
  'other_obsolescence_pl-sql',
  'other_obsolescence_postgresql',
  'other_obsolescence_post-script',
  'other_obsolescence_pov-ray-sdl',
  'other_obsolescence_power-bi-report-server',
  'other_obsolescence_power-builder',
  'other_obsolescence_powercenter',
  'other_obsolescence_power-shell',
  'other_obsolescence_prolog',
  'other_obsolescence_protocol-buffer',
  'other_obsolescence_pug',
  'other_obsolescence_puppet',
  'other_obsolescence_pure-basic',
  'other_obsolescence_pure-data',
  'other_obsolescence_python',
  'other_obsolescence_q',
  'other_obsolescence_qmake',
  'other_obsolescence_qml',
  'other_obsolescence_qt-script',
  'other_obsolescence_r',
  'other_obsolescence_rabbit-mq',
  'other_obsolescence_raku',
  'other_obsolescence_reason',
  'other_obsolescence_rebol',
  'other_obsolescence_redcode',
  'other_obsolescence_rescript',
  'other_obsolescence_rexx',
  'other_obsolescence_rich-text-format',
  'other_obsolescence_robot-framework',
  'other_obsolescence_roff',
  'other_obsolescence_rpc',
  'other_obsolescence_ruby',
  'other_obsolescence_rust',
  'other_obsolescence_sas',
  'other_obsolescence_sass',
  'other_obsolescence_scala',
  'other_obsolescence_scheme',
  'other_obsolescence_scilab',
  'other_obsolescence_scss',
  'other_obsolescence_sed',
  'other_obsolescence_server-gen',
  'other_obsolescence_shell',
  'other_obsolescence_slash',
  'other_obsolescence_slice',
  'other_obsolescence_smalltalk',
  'other_obsolescence_smarty',
  'other_obsolescence_smpl',
  'other_obsolescence_source-pawn',
  'other_obsolescence_sql',
  'other_obsolescence_sqlpl',
  'other_obsolescence_sql-server-analysis-services',
  'other_obsolescence_sql-server-integration-services',
  'other_obsolescence_sql-server-reporting-services',
  'other_obsolescence_srecode-template',
  'other_obsolescence_standard-ml',
  'other_obsolescence_starlark',
  'other_obsolescence_stata',
  'other_obsolescence_sterling-file-gateway',
  'other_obsolescence_string-template',
  'other_obsolescence_stylus',
  'other_obsolescence_svelte',
  'other_obsolescence_swift',
  'other_obsolescence_swig',
  'other_obsolescence_synchronization-services-for-ado-dot-net',
  'other_obsolescence_system-verilog',
  'other_obsolescence_talend-bigdata',
  'other_obsolescence_tcl',
  'other_obsolescence_tex',
  'other_obsolescence_thrift',
  'other_obsolescence_tibco-rdv-middleware',
  'other_obsolescence_tla',
  'other_obsolescence_tomcat',
  'other_obsolescence_tsa',
  'other_obsolescence_tsql',
  'other_obsolescence_tuxedo-dot-net-client',
  'other_obsolescence_twig',
  'other_obsolescence_typescript',
  'other_obsolescence_ui-for-asp-dot-net-mvc',
  'other_obsolescence_v',
  'other_obsolescence_vba',
  'other_obsolescence_vb-script',
  'other_obsolescence_verilog',
  'other_obsolescence_vim-script',
  'other_obsolescence_vim-snippet',
  'other_obsolescence_vision-for-dot-net',
  'other_obsolescence_visual-basic',
  'other_obsolescence_visual-basic-6',
  'other_obsolescence_visual-basic-dot-net',
  'other_obsolescence_visual-studio-ado-dot-net-entity-framework-tools',
  'other_obsolescence_vue',
  'other_obsolescence_web-assembly',
  'other_obsolescence_weblogic-server',
  'other_obsolescence_webmethods',
  'other_obsolescence_web-middleware',
  'other_obsolescence_windows',
  'other_obsolescence_windows-azure-libraries-for-dot-net',
  'other_obsolescence_xap-dot-net',
  'other_obsolescence_x-base',
  'other_obsolescence_xonsh',
  'other_obsolescence_x-proc',
  'other_obsolescence_x-query',
  'other_obsolescence_xs',
  'other_obsolescence_xslt',
  'other_obsolescence_xtend',
  'other_obsolescence_yacc',
  'other_obsolescence_zen-script',
  'tco_gbsu-cost_non-workforce',
  'tco_gbsu-cost_non-workforce-forecast',
  'tco_gbsu-cost_non-workforce-prev-year',
  'tco_gbsu-cost_workforce',
  'tco_gbsu-cost_workforce-forecast',
  'tco_gbsu-cost_workforce-prev-year',
  'tco_infrastructure-cost_azure',
  'tco_infrastructure-cost_azure-forecast',
  'tco_infrastructure-cost_azure-prev-year',
  'tco_infrastructure-cost_aws',
  'tco_infrastructure-cost_aws-forecast',
  'tco_infrastructure-cost_aws-prev-year',
  'tco_infrastructure-cost_gts',
  'tco_infrastructure-cost_gts-forecast',
  'tco_infrastructure-cost_gts-prev-year',
  'tco_tbs_tbs-enhancement',
  'tco_tbs_tbs-financial',
  'tco_tbs_tbs-infrastructure',
  'tco_tbs_tbs-management',
  'tco_tbs_tbs-project',
  'tco_tbs_tbs-support',
  'tco_tbs_tbs-technical',
  'tco_tbs_tbs-enhancement-duplicate',
  'tco_tbs_tbs-financial-duplicate',
  'tco_tbs_tbs-infrastructure-duplicate',
  'tco_tbs_tbs-management-duplicate',
  'tco_tbs_tbs-project-duplicate',
  'tco_tbs_tbs-support-duplicate',
  'tco_tbs_tbs-technical-duplicate',
  'tco_vendor-cost_software',
  'tco_vendor-cost_software-forecast',
  'tco_vendor-cost_software-prev-year',
  'iAppliName',
  'languages',
  'other_deviance-standard_1e-oss',
  'other_deviance-standard_2emotion',
  'other_deviance-standard_3skey',
  'other_deviance-standard_ab-initio',
  'other_deviance-standard_abbyy-timeline',
  'other_deviance-standard_abend-aid',
  'other_deviance-standard_abendaid',
  'other_deviance-standard_abinitio',
  'other_deviance-standard_activecard',
  'other_deviance-standard_activepivot',
  'other_deviance-standard_adn-authentification-denormalise',
  'other_deviance-standard_adobe-acrobat',
  'other_deviance-standard_adoptopenjdk',
  'other_deviance-standard_ag-grid',
  'other_deviance-standard_aldon',
  'other_deviance-standard_alerta',
  'other_deviance-standard_amazon-api-gateway',
  'other_deviance-standard_amazon-cloudfront',
  'other_deviance-standard_amazon-cloudwatch',
  'other_deviance-standard_amazon-data-lifecycle-manager',
  'other_deviance-standard_amazon-documentdb',
  'other_deviance-standard_amazon-documentdb-with-mongodb-compatibility',
  'other_deviance-standard_amazon-dynamodb',
  'other_deviance-standard_amazon-ebs',
  'other_deviance-standard_amazon-ec2',
  'other_deviance-standard_amazon-ec2-ami',
  'other_deviance-standard_amazon-ec2-auto-scaling',
  'other_deviance-standard_amazon-ecr',
  'other_deviance-standard_amazon-ecs',
  'other_deviance-standard_amazon-efs',
  'other_deviance-standard_amazon-eks',
  'other_deviance-standard_amazon-elasticache',
  'other_deviance-standard_amazon-eventbridge-cloudwatch-events',
  'other_deviance-standard_amazon-eventbridge-scheduler',
  'other_deviance-standard_amazon-eventbridge-schema-registry',
  'other_deviance-standard_amazon-guardduty',
  'other_deviance-standard_amazon-keyspaces',
  'other_deviance-standard_amazon-neptune',
  'other_deviance-standard_amazon-rds',
  'other_deviance-standard_amazon-rds-aurora-postgresql',
  'other_deviance-standard_amazon-rds-for-postgresql',
  'other_deviance-standard_amazon-route-53',
  'other_deviance-standard_amazon-s3',
  'other_deviance-standard_amazon-s3-glacier',
  'other_deviance-standard_amazon-sns',
  'other_deviance-standard_amazon-sqs',
  'other_deviance-standard_amazon-translate',
  'other_deviance-standard_amazon-vpc',
  'other_deviance-standard_ambionics',
  'other_deviance-standard_ami-defender',
  'other_deviance-standard_anaconda',
  'other_deviance-standard_angular-js',
  'other_deviance-standard_angularjs',
  'other_deviance-standard_ansible',
  'other_deviance-standard_anywhere365',
  'other_deviance-standard_apache-airflow',
  'other_deviance-standard_apache-commons',
  'other_deviance-standard_apache-cordova',
  'other_deviance-standard_apache-druid',
  'other_deviance-standard_apache-flink',
  'other_deviance-standard_apache-httpd',
  'other_deviance-standard_apache-ignite',
  'other_deviance-standard_apache-jakarta',
  'other_deviance-standard_apache-jmeter',
  'other_deviance-standard_apache-kafka',
  'other_deviance-standard_apache-netbeans',
  'other_deviance-standard_apache-nifi',
  'other_deviance-standard_apache-tomcat',
  'other_deviance-standard_apache-trino',
  'other_deviance-standard_api-connect',
  'other_deviance-standard_apibank',
  'other_deviance-standard_appdynamics',
  'other_deviance-standard_application-discovery-connect-adc',
  'other_deviance-standard_appveyor',
  'other_deviance-standard_argo-cd',
  'other_deviance-standard_artifactory',
  'other_deviance-standard_aruba-hpe',
  'other_deviance-standard_asg-life-cycle-manager',
  'other_deviance-standard_aspose',
  'other_deviance-standard_auto-soft-capping',
  'other_deviance-standard_autocad',
  'other_deviance-standard_automation-anywhere',
  'other_deviance-standard_autonomy-idol-server',
  'other_deviance-standard_avi-network',
  'other_deviance-standard_avro',
  'other_deviance-standard_aws-backup',
  'other_deviance-standard_aws-billing-and-cost-management-data-exports',
  'other_deviance-standard_aws-budgets',
  'other_deviance-standard_aws-certificate-manager-acm',
  'other_deviance-standard_aws-cloudformation',
  'other_deviance-standard_aws-cloudtrail',
  'other_deviance-standard_aws-config',
  'other_deviance-standard_aws-cost-and-usage-report',
  'other_deviance-standard_aws-cost-explorer',
  'other_deviance-standard_aws-direct-connect',
  'other_deviance-standard_aws-health',
  'other_deviance-standard_aws-identity-and-access-management-iam',
  'other_deviance-standard_aws-key-management-service-aws-kms',
  'other_deviance-standard_aws-lambda',
  'other_deviance-standard_aws-network-firewall',
  'other_deviance-standard_aws-organizations',
  'other_deviance-standard_aws-resource-groups',
  'other_deviance-standard_aws-savings-plans',
  'other_deviance-standard_aws-secrets-manager',
  'other_deviance-standard_aws-security-hub',
  'other_deviance-standard_aws-security-token-service',
  'other_deviance-standard_aws-service-catalog',
  'other_deviance-standard_aws-step-functions',
  'other_deviance-standard_aws-support',
  'other_deviance-standard_aws-transit-gateway',
  'other_deviance-standard_aws-trusted-advisor',
  'other_deviance-standard_axway-accounting-integration-suite',
  'other_deviance-standard_azure-ai-bot-service',
  'other_deviance-standard_azure-ai-document-intelligence',
  'other_deviance-standard_azure-ai-search',
  'other_deviance-standard_azure-ai-services-speech',
  'other_deviance-standard_azure-ai-translator',
  'other_deviance-standard_azure-ai-vision',
  'other_deviance-standard_azure-ai-vision-ocr',
  'other_deviance-standard_azure-ai-vision-ocr-',
  'other_deviance-standard_azure-analysis-services',
  'other_deviance-standard_azure-app-configuration',
  'other_deviance-standard_azure-app-service',
  'other_deviance-standard_azure-app-service-certificates',
  'other_deviance-standard_azure-app-service-plans',
  'other_deviance-standard_azure-application-gateway',
  'other_deviance-standard_azure-automation',
  'other_deviance-standard_azure-backup',
  'other_deviance-standard_azure-backup-recovery-services-vaults',
  'other_deviance-standard_azure-backup-vault',
  'other_deviance-standard_azure-batch',
  'other_deviance-standard_azure-cache-for-redis',
  'other_deviance-standard_azure-cdn',
  'other_deviance-standard_azure-compute-gallery',
  'other_deviance-standard_azure-conditional-access',
  'other_deviance-standard_azure-cosmos-db',
  'other_deviance-standard_azure-cost-management-billing',
  'other_deviance-standard_azure-data-explorer',
  'other_deviance-standard_azure-database-for-postgresql',
  'other_deviance-standard_azure-databricks',
  'other_deviance-standard_azure-ddos-protection',
  'other_deviance-standard_azure-dns',
  'other_deviance-standard_azure-event-grid',
  'other_deviance-standard_azure-event-grid-custom-topic',
  'other_deviance-standard_azure-event-grid-domain',
  'other_deviance-standard_azure-event-grid-system-topic',
  'other_deviance-standard_azure-event-hubs',
  'other_deviance-standard_azure-expressroute',
  'other_deviance-standard_azure-firewall',
  'other_deviance-standard_azure-front-door',
  'other_deviance-standard_azure-functions',
  'other_deviance-standard_azure-information-protection-aip-rms',
  'other_deviance-standard_azure-key-vault',
  'other_deviance-standard_azure-kubernetes-service',
  'other_deviance-standard_azure-load-balancer',
  'other_deviance-standard_azure-logic-apps',
  'other_deviance-standard_azure-managed-applications',
  'other_deviance-standard_azure-management-groups',
  'other_deviance-standard_azure-maps',
  'other_deviance-standard_azure-monitor',
  'other_deviance-standard_azure-monitor-application-insights',
  'other_deviance-standard_azure-monitor-log-analytics-workspace',
  'other_deviance-standard_azure-multi-factor-authentication-mfa',
  'other_deviance-standard_azure-multifactor-authentication-mfa',
  'other_deviance-standard_azure-netapp-files',
  'other_deviance-standard_azure-network-interface',
  'other_deviance-standard_azure-network-watcher',
  'other_deviance-standard_azure-notification-hubs',
  'other_deviance-standard_azure-openai-service',
  'other_deviance-standard_azure-private-dns',
  'other_deviance-standard_azure-private-link',
  'other_deviance-standard_azure-private-link-private-endpoint',
  'other_deviance-standard_azure-queue-storage',
  'other_deviance-standard_azure-redis-cache',
  'other_deviance-standard_azure-resource-manager',
  'other_deviance-standard_azure-savings-plans',
  'other_deviance-standard_azure-service-bus',
  'other_deviance-standard_azure-service-fabric',
  'other_deviance-standard_azure-signalr-service',
  'other_deviance-standard_azure-sql',
  'other_deviance-standard_azure-storage',
  'other_deviance-standard_azure-synapse-analytics',
  'other_deviance-standard_azure-virtual-desktop',
  'other_deviance-standard_azure-virtual-machine-scale-sets',
  'other_deviance-standard_azure-virtual-machines',
  'other_deviance-standard_azure-virtual-network',
  'other_deviance-standard_azure-virtual-network-network-security-group',
  'other_deviance-standard_azure-virtual-network-public-ip-addresses',
  'other_deviance-standard_azure-virtual-network-route-table',
  'other_deviance-standard_azure-web-apps',
  'other_deviance-standard_babel-js',
  'other_deviance-standard_backbone-js',
  'other_deviance-standard_backbonejs',
  'other_deviance-standard_bcv5',
  'other_deviance-standard_big-ip-afm',
  'other_deviance-standard_big-ip-apm',
  'other_deviance-standard_big-ip-asm',
  'other_deviance-standard_big-ip-gtm',
  'other_deviance-standard_big-ip-ltm',
  'other_deviance-standard_bigip-afm',
  'other_deviance-standard_bigip-apm-access-policy-manager',
  'other_deviance-standard_bigip-asm-application-security-manager',
  'other_deviance-standard_bigip-gtm-global-traffic-manager',
  'other_deviance-standard_bigip-ltm',
  'other_deviance-standard_birt',
  'other_deviance-standard_bitlocker',
  'other_deviance-standard_bmc-controlj',
  'other_deviance-standard_bmc-controlm',
  'other_deviance-standard_bmc-event-manager',
  'other_deviance-standard_bmc-patrol',
  'other_deviance-standard_bootstrap',
  'other_deviance-standard_bower',
  'other_deviance-standard_ca-autosys',
  'other_deviance-standard_ca-computer-associates',
  'other_deviance-standard_ca-tpx',
  'other_deviance-standard_camunda',
  'other_deviance-standard_cassandra',
  'other_deviance-standard_cast',
  'other_deviance-standard_centos',
  'other_deviance-standard_change-auditor',
  'other_deviance-standard_checkmarx',
  'other_deviance-standard_checkpoint',
  'other_deviance-standard_cics',
  'other_deviance-standard_ciphertrust',
  'other_deviance-standard_cisco',
  'other_deviance-standard_cisco-ironport',
  'other_deviance-standard_cisco-vta-polycom-or-tandberg-rooms-system-with-webcam',
  'other_deviance-standard_cisco-vta-video-telephony-advantage',
  'other_deviance-standard_citrix-access-gateway',
  'other_deviance-standard_citrix-xen-desktop',
  'other_deviance-standard_citrix-xenapp',
  'other_deviance-standard_ckeditor',
  'other_deviance-standard_clickhouse',
  'other_deviance-standard_cloud-init',
  'other_deviance-standard_cloudera-data-platform',
  'other_deviance-standard_cnapp-prisma-cloud',
  'other_deviance-standard_coderpad',
  'other_deviance-standard_codetogether',
  'other_deviance-standard_cohesity',
  'other_deviance-standard_collibra-data-governance',
  'other_deviance-standard_compuware-iam',
  'other_deviance-standard_conda-forge',
  'other_deviance-standard_condaforge',
  'other_deviance-standard_confluence',
  'other_deviance-standard_consul',
  'other_deviance-standard_contextor',
  'other_deviance-standard_controlsa',
  'other_deviance-standard_core-nlg',
  'other_deviance-standard_corenlg',
  'other_deviance-standard_corporate-tools-via-good',
  'other_deviance-standard_couchbase',
  'other_deviance-standard_coverlet',
  'other_deviance-standard_crucible-ex-fisheye',
  'other_deviance-standard_cucumber',
  'other_deviance-standard_cypress',
  'other_deviance-standard_dapper',
  'other_deviance-standard_databricks',
  'other_deviance-standard_databricks-on-aws',
  'other_deviance-standard_dataiku',
  'other_deviance-standard_dbiq',
  'other_deviance-standard_debian',
  'other_deviance-standard_dell-emc-powermax',
  'other_deviance-standard_denyall-rweb',
  'other_deviance-standard_dependabot',
  'other_deviance-standard_deployit',
  'other_deviance-standard_diamar',
  'other_deviance-standard_didomi',
  'other_deviance-standard_doc1',
  'other_deviance-standard_docaposte-trust-platform',
  'other_deviance-standard_docker-engine',
  'other_deviance-standard_documentum-content-server',
  'other_deviance-standard_docusign',
  'other_deviance-standard_dollar-universe',
  'other_deviance-standard_dp360',
  'other_deviance-standard_dropwizard',
  'other_deviance-standard_easyconnect',
  'other_deviance-standard_easylience',
  'other_deviance-standard_eclipse',
  'other_deviance-standard_eclipse-openj9',
  'other_deviance-standard_edge-chromium',
  'other_deviance-standard_efficient-ip',
  'other_deviance-standard_ejtechnologies-jprofiler',
  'other_deviance-standard_elastic-load-balancing-elb',
  'other_deviance-standard_elastic-search-enterprise',
  'other_deviance-standard_elasticsearch',
  'other_deviance-standard_elasticsearch-kibana',
  'other_deviance-standard_emc-centera',
  'other_deviance-standard_emc-documentum-content-server',
  'other_deviance-standard_emc-emailxtender',
  'other_deviance-standard_emc-eroom',
  'other_deviance-standard_emc-rsa',
  'other_deviance-standard_emc-vmax',
  'other_deviance-standard_emc-vnx',
  'other_deviance-standard_emulateur-3270',
  'other_deviance-standard_endevor',
  'other_deviance-standard_enterprise-meta-environment-portal',
  'other_deviance-standard_envinstall',
  'other_deviance-standard_eos',
  'other_deviance-standard_esbuild',
  'other_deviance-standard_eslint',
  'other_deviance-standard_esxvcenter',
  'other_deviance-standard_euda',
  'other_deviance-standard_evidian-e-sso',
  'other_deviance-standard_evidian-esso',
  'other_deviance-standard_exstream',
  'other_deviance-standard_extreme-application-platform-xap-gigaspaces',
  'other_deviance-standard_falco',
  'other_deviance-standard_fasterxml',
  'other_deviance-standard_ff4j',
  'other_deviance-standard_figma',
  'other_deviance-standard_file-aid',
  'other_deviance-standard_fileaid',
  'other_deviance-standard_filebeat',
  'other_deviance-standard_fitnesse',
  'other_deviance-standard_flask',
  'other_deviance-standard_flexera',
  'other_deviance-standard_flexserv',
  'other_deviance-standard_fluentd-td-agent',
  'other_deviance-standard_forgerock-am',
  'other_deviance-standard_forgerock-ds',
  'other_deviance-standard_forgerock-idm',
  'other_deviance-standard_fortigate',
  'other_deviance-standard_fortinet',
  'other_deviance-standard_galytix',
  'other_deviance-standard_gatling',
  'other_deviance-standard_gigamon',
  'other_deviance-standard_git',
  'other_deviance-standard_gitguardian',
  'other_deviance-standard_github',
  'other_deviance-standard_github-actions',
  'other_deviance-standard_github-copilot',
  'other_deviance-standard_github-project',
  'other_deviance-standard_gitlab',
  'other_deviance-standard_gitversion',
  'other_deviance-standard_globalrelay',
  'other_deviance-standard_gnome-json-glib',
  'other_deviance-standard_go-lang',
  'other_deviance-standard_golismero',
  'other_deviance-standard_google-chrome',
  'other_deviance-standard_google-search-appliance',
  'other_deviance-standard_google-web-toolkit-gwt',
  'other_deviance-standard_gpo-admin',
  'other_deviance-standard_graalvm',
  'other_deviance-standard_grafana',
  'other_deviance-standard_graphnet',
  'other_deviance-standard_groovy',
  'other_deviance-standard_gulp',
  'other_deviance-standard_gunicorn',
  'other_deviance-standard_h2',
  'other_deviance-standard_ha-proxy',
  'other_deviance-standard_hadoop-hortonworks',
  'other_deviance-standard_hangfire',
  'other_deviance-standard_haproxy',
  'other_deviance-standard_harbor',
  'other_deviance-standard_hashicorp-packer',
  'other_deviance-standard_hazelcast',
  'other_deviance-standard_hbase',
  'other_deviance-standard_hdinsight',
  'other_deviance-standard_hdparm',
  'other_deviance-standard_hello-for-busines-whfb',
  'other_deviance-standard_helm',
  'other_deviance-standard_highcharts',
  'other_deviance-standard_hive-metastore-hms',
  'other_deviance-standard_hopex-bpa',
  'other_deviance-standard_hopex-database-builder',
  'other_deviance-standard_hopex-it-management',
  'other_deviance-standard_hostaccess-for-the-cloud',
  'other_deviance-standard_hp-blxxx-serie',
  'other_deviance-standard_hp-loadrunner',
  'other_deviance-standard_hp-quality-center-former-test-director',
  'other_deviance-standard_hp-server-automation',
  'other_deviance-standard_hp-service-management',
  'other_deviance-standard_hp-sitescope-system-availability-management',
  'other_deviance-standard_hpux',
  'other_deviance-standard_hsm',
  'other_deviance-standard_htmlagilitypack',
  'other_deviance-standard_iannotate',
  'other_deviance-standard_ibm-aix',
  'other_deviance-standard_ibm-datapower',
  'other_deviance-standard_ibm-datastage',
  'other_deviance-standard_ibm-db2',
  'other_deviance-standard_ibm-db2-for-zos',
  'other_deviance-standard_ibm-enterprise-cobol',
  'other_deviance-standard_ibm-i-as400',
  'other_deviance-standard_ibm-ilog-cplex',
  'other_deviance-standard_ibm-mq',
  'other_deviance-standard_ibm-openpages',
  'other_deviance-standard_ibm-p-series',
  'other_deviance-standard_ibm-rational-software-architect-rsa',
  'other_deviance-standard_ibm-rexx-restructured-extended-executor',
  'other_deviance-standard_ibm-sterling-connectdirect',
  'other_deviance-standard_ibm-sterling-connectexpress-tom',
  'other_deviance-standard_ibm-websphere-mq',
  'other_deviance-standard_ibm-zos',
  'other_deviance-standard_idemia',
  'other_deviance-standard_identityiq',
  'other_deviance-standard_ilex',
  'other_deviance-standard_illumio',
  'other_deviance-standard_imperva',
  'other_deviance-standard_ims',
  'other_deviance-standard_influxdb',
  'other_deviance-standard_infoarchive',
  'other_deviance-standard_infoblox',
  'other_deviance-standard_infoprint',
  'other_deviance-standard_informatica-data-quality-idq',
  'other_deviance-standard_informatica-mdm-hub',
  'other_deviance-standard_informatica-powercenter',
  'other_deviance-standard_inhouse-tmon-solution',
  'other_deviance-standard_intellij',
  'other_deviance-standard_intermapper',
  'other_deviance-standard_invicti',
  'other_deviance-standard_ionic',
  'other_deviance-standard_ip-trade-opentrade',
  'other_deviance-standard_ipacref',
  'other_deviance-standard_isc-e-gen',
  'other_deviance-standard_isc-egen',
  'other_deviance-standard_jaggaer',
  'other_deviance-standard_jasmine',
  'other_deviance-standard_jasperreports',
  'other_deviance-standard_java',
  'other_deviance-standard_javaee-platform',
  'other_deviance-standard_javascript',
  'other_deviance-standard_jboss-eap',
  'other_deviance-standard_jboss-wildfly',
  'other_deviance-standard_jenkins',
  'other_deviance-standard_jenkins-ex-hudson',
  'other_deviance-standard_jest',
  'other_deviance-standard_jfrog-xray',
  'other_deviance-standard_jira',
  'other_deviance-standard_job-scheduler',
  'other_deviance-standard_jooq',
  'other_deviance-standard_jprofiler',
  'other_deviance-standard_jquery-ui',
  'other_deviance-standard_jsonpath-dotnet',
  'other_deviance-standard_jsonpointer-dotnet',
  'other_deviance-standard_juniper',
  'other_deviance-standard_junit',
  'other_deviance-standard_kahoot',
  'other_deviance-standard_karma',
  'other_deviance-standard_kedro',
  'other_deviance-standard_keyvault-managed-hsm',
  'other_deviance-standard_kibana',
  'other_deviance-standard_klaxoon',
  'other_deviance-standard_kubernetes',
  'other_deviance-standard_kubernetes-cronjobs',
  'other_deviance-standard_leanix',
  'other_deviance-standard_libcurl',
  'other_deviance-standard_libxml',
  'other_deviance-standard_lighthouse',
  'other_deviance-standard_lightstreamer',
  'other_deviance-standard_linux',
  'other_deviance-standard_liquibase',
  'other_deviance-standard_loadrunner',
  'other_deviance-standard_lodash',
  'other_deviance-standard_log4j',
  'other_deviance-standard_logback',
  'other_deviance-standard_lombok',
  'other_deviance-standard_m365-apps-for-entreprise',
  'other_deviance-standard_mac-afee-nsp',
  'other_deviance-standard_mailjet',
  'other_deviance-standard_mailkit',
  'other_deviance-standard_mainview-for-zos',
  'other_deviance-standard_marley',
  'other_deviance-standard_matomo',
  'other_deviance-standard_maven',
  'other_deviance-standard_maxx',
  'other_deviance-standard_mbunit-oss',
  'other_deviance-standard_mcafee-nsp',
  'other_deviance-standard_mercury',
  'other_deviance-standard_metricbeat',
  'other_deviance-standard_mf-functional-testing',
  'other_deviance-standard_mf-ppm-center',
  'other_deviance-standard_mf-ppm-time-tracking-module',
  'other_deviance-standard_mf-ppm-timetracking-module',
  'other_deviance-standard_mf-quality-center',
  'other_deviance-standard_micromamba',
  'other_deviance-standard_microsoft-active-directory',
  'other_deviance-standard_microsoft-api-management',
  'other_deviance-standard_microsoft-azure-portal',
  'other_deviance-standard_microsoft-bginfo',
  'other_deviance-standard_microsoft-build',
  'other_deviance-standard_microsoft-defender-for-cloud',
  'other_deviance-standard_microsoft-defender-for-container',
  'other_deviance-standard_microsoft-defender-for-endpoint',
  'other_deviance-standard_microsoft-defender-for-endpoints',
  'other_deviance-standard_microsoft-dotnet',
  'other_deviance-standard_microsoft-dotnet-ef',
  'other_deviance-standard_microsoft-dotnet-framework',
  'other_deviance-standard_microsoft-dotnet-wcf',
  'other_deviance-standard_microsoft-dotnet-winforms',
  'other_deviance-standard_microsoft-dotnet-wpf',
  'other_deviance-standard_microsoft-edge-chromium',
  'other_deviance-standard_microsoft-entra-domain-services',
  'other_deviance-standard_microsoft-entra-external-id',
  'other_deviance-standard_microsoft-entra-id',
  'other_deviance-standard_microsoft-entra-id-managed-identities-for-azure-resources',
  'other_deviance-standard_microsoft-exchange',
  'other_deviance-standard_microsoft-fabric',
  'other_deviance-standard_microsoft-forefront-security-for-exchange',
  'other_deviance-standard_microsoft-iis',
  'other_deviance-standard_microsoft-internet-explorer',
  'other_deviance-standard_microsoft-intune',
  'other_deviance-standard_microsoft-office',
  'other_deviance-standard_microsoft-powerbi',
  'other_deviance-standard_microsoft-powerplatform',
  'other_deviance-standard_microsoft-scom',
  'other_deviance-standard_microsoft-sentinel',
  'other_deviance-standard_microsoft-sharepoint',
  'other_deviance-standard_microsoft-signalr',
  'other_deviance-standard_microsoft-silverlight',
  'other_deviance-standard_microsoft-skype-for-business',
  'other_deviance-standard_microsoft-sql-server',
  'other_deviance-standard_microsoft-ssas',
  'other_deviance-standard_microsoft-ssis',
  'other_deviance-standard_microsoft-ssrs',
  'other_deviance-standard_microsoft-teams',
  'other_deviance-standard_microsoft-teams-room',
  'other_deviance-standard_microsoft-visio',
  'other_deviance-standard_microsoft-visual-cplusplus-runtime',
  'other_deviance-standard_microsoft-visual-studio',
  'other_deviance-standard_microsoft-windows',
  'other_deviance-standard_microsoft-windows-defender-atp',
  'other_deviance-standard_microsoft-windows-server',
  'other_deviance-standard_microsoft-windows-store-for-business',
  'other_deviance-standard_microstrategy',
  'other_deviance-standard_miniforge',
  'other_deviance-standard_mlflow',
  'other_deviance-standard_mockhttp',
  'other_deviance-standard_mockito',
  'other_deviance-standard_mongodb',
  'other_deviance-standard_moq',
  'other_deviance-standard_mozilla-firefox',
  'other_deviance-standard_mqseries-for-mainframe',
  'other_deviance-standard_nagios',
  'other_deviance-standard_neo4j-database',
  'other_deviance-standard_neoxam-gp3',
  'other_deviance-standard_netapp-fas-series',
  'other_deviance-standard_netapp-fas2000-serie',
  'other_deviance-standard_netscaler',
  'other_deviance-standard_netscout',
  'other_deviance-standard_netshot',
  'other_deviance-standard_netsparker',
  'other_deviance-standard_nexus',
  'other_deviance-standard_nginx',
  'other_deviance-standard_nlog',
  'other_deviance-standard_node-js',
  'other_deviance-standard_nodejs',
  'other_deviance-standard_npm-yarn',
  'other_deviance-standard_nsx-t',
  'other_deviance-standard_nsxt',
  'other_deviance-standard_nunit',
  'other_deviance-standard_nutanix',
  'other_deviance-standard_octopus-deploy',
  'other_deviance-standard_odm',
  'other_deviance-standard_office-365-proplus',
  'other_deviance-standard_onedrive',
  'other_deviance-standard_onenote',
  'other_deviance-standard_open-am',
  'other_deviance-standard_open-jdk-oracle',
  'other_deviance-standard_open-jdk-redhat',
  'other_deviance-standard_open-policy-agent-opa',
  'other_deviance-standard_open-telemetry',
  'other_deviance-standard_open-text-rightfax',
  'other_deviance-standard_open-text-vignette',
  'other_deviance-standard_opensearch',
  'other_deviance-standard_opensee',
  'other_deviance-standard_openshift',
  'other_deviance-standard_openstack',
  'other_deviance-standard_opensvc',
  'other_deviance-standard_opentext-business-intelligence',
  'other_deviance-standard_opentext-vignette',
  'other_deviance-standard_optim',
  'other_deviance-standard_oracle-bi-suite-enterprise-edition-obiee',
  'other_deviance-standard_oracle-database-enterprise-server',
  'other_deviance-standard_oracle-dataguard',
  'other_deviance-standard_oracle-directory-services-plus',
  'other_deviance-standard_oracle-essbase',
  'other_deviance-standard_oracle-exacc',
  'other_deviance-standard_oracle-exadata-appliance',
  'other_deviance-standard_oracle-forms-and-reports',
  'other_deviance-standard_oracle-hyperion-planning',
  'other_deviance-standard_oracle-jdk',
  'other_deviance-standard_oracle-solaris',
  'other_deviance-standard_oracle-sparc',
  'other_deviance-standard_oracle-weblogic-portal',
  'other_deviance-standard_os-features-zos-partition-aix-power-vm-solaris-zones',
  'other_deviance-standard_outlook-365',
  'other_deviance-standard_owlint',
  'other_deviance-standard_pdsman',
  'other_deviance-standard_pega',
  'other_deviance-standard_pexip',
  'other_deviance-standard_physical-server',
  'other_deviance-standard_pickyourskills',
  'other_deviance-standard_pingdom',
  'other_deviance-standard_pixi',
  'other_deviance-standard_platform-symphony',
  'other_deviance-standard_playwright',
  'other_deviance-standard_postal-mail',
  'other_deviance-standard_postgresql',
  'other_deviance-standard_power-apps',
  'other_deviance-standard_power-automate',
  'other_deviance-standard_powerbuilder',
  'other_deviance-standard_powershell',
  'other_deviance-standard_pro-transform',
  'other_deviance-standard_pro-transform-afp2pdf',
  'other_deviance-standard_prometheus',
  'other_deviance-standard_protractor',
  'other_deviance-standard_prtg',
  'other_deviance-standard_pull-printing',
  'other_deviance-standard_puppet',
  'other_deviance-standard_pyramid',
  'other_deviance-standard_python',
  'other_deviance-standard_qualys',
  'other_deviance-standard_quantive',
  'other_deviance-standard_quarkus',
  'other_deviance-standard_quartz',
  'other_deviance-standard_rabbitmq',
  'other_deviance-standard_racf-for-zos',
  'other_deviance-standard_rational-clearcase',
  'other_deviance-standard_react-js',
  'other_deviance-standard_reactjs',
  'other_deviance-standard_recovery-manager-for-ad',
  'other_deviance-standard_red-hat-enterprise-linux',
  'other_deviance-standard_redis',
  'other_deviance-standard_redux',
  'other_deviance-standard_restsharp',
  'other_deviance-standard_retarus',
  'other_deviance-standard_rim-blackberry',
  'other_deviance-standard_rimraf',
  'other_deviance-standard_rsd-folders',
  'other_deviance-standard_rstudio',
  'other_deviance-standard_rtfe',
  'other_deviance-standard_rtfesesame-api-ldap-interface-as-fallback',
  'other_deviance-standard_rust',
  'other_deviance-standard_samsung-android',
  'other_deviance-standard_sap-business-objects-bi-bo-bi',
  'other_deviance-standard_sas-enterprise-miner',
  'other_deviance-standard_satis',
  'other_deviance-standard_saxon',
  'other_deviance-standard_scala',
  'other_deviance-standard_scality-s3',
  'other_deviance-standard_scanmail-smex',
  'other_deviance-standard_securedisk',
  'other_deviance-standard_securedoc',
  'other_deviance-standard_securedoc-prim39x-zonecentral',
  'other_deviance-standard_securehub',
  'other_deviance-standard_securemail',
  'other_deviance-standard_securemobile-smime',
  'other_deviance-standard_secureshare',
  'other_deviance-standard_securetrack',
  'other_deviance-standard_securid',
  'other_deviance-standard_selenium',
  'other_deviance-standard_semarchy',
  'other_deviance-standard_serilog',
  'other_deviance-standard_service-azure-kubernetes-aks',
  'other_deviance-standard_service-management-smax',
  'other_deviance-standard_servicenow',
  'other_deviance-standard_sg-api-catalog',
  'other_deviance-standard_sg-apim',
  'other_deviance-standard_sg-bootstrap',
  'other_deviance-standard_sg-connect',
  'other_deviance-standard_sg-dashboard',
  'other_deviance-standard_sg-docs',
  'other_deviance-standard_sg-eficaas',
  'other_deviance-standard_sg-everest',
  'other_deviance-standard_sg-farms',
  'other_deviance-standard_sg-ftaas',
  'other_deviance-standard_sg-iam',
  'other_deviance-standard_sg-observability',
  'other_deviance-standard_sg-safe',
  'other_deviance-standard_sg-signin',
  'other_deviance-standard_sg-task-aggregator',
  'other_deviance-standard_sg-workflow',
  'other_deviance-standard_sgapim',
  'other_deviance-standard_sgconnect',
  'other_deviance-standard_sgcp-access-control',
  'other_deviance-standard_sgcp-access-governance',
  'other_deviance-standard_sgcp-accounts',
  'other_deviance-standard_sgcp-advisory',
  'other_deviance-standard_sgcp-bastion-cyberark',
  'other_deviance-standard_sgcp-certificates',
  'other_deviance-standard_sgcp-dataviz',
  'other_deviance-standard_sgcp-dns',
  'other_deviance-standard_sgcp-doitnow',
  'other_deviance-standard_sgcp-file-storage',
  'other_deviance-standard_sgcp-ipam',
  'other_deviance-standard_sgcp-lucid',
  'other_deviance-standard_sgcp-metrology',
  'other_deviance-standard_sgcp-monitoring',
  'other_deviance-standard_sgcp-myvault',
  'other_deviance-standard_sgcp-nsg',
  'other_deviance-standard_sgcp-object-storage',
  'other_deviance-standard_sgcp-observability',
  'other_deviance-standard_sgcp-ocs-virtual-server',
  'other_deviance-standard_sgcp-openstack-block-storage',
  'other_deviance-standard_sgcp-oracle-cloud-platform',
  'other_deviance-standard_sgcp-orchestrated-containers-ocs',
  'other_deviance-standard_sgcp-os-configuration',
  'other_deviance-standard_sgcp-os-factory',
  'other_deviance-standard_sgcp-postgresql-cloud-platform-pcp',
  'other_deviance-standard_sgcp-python-sdk',
  'other_deviance-standard_sgcp-rabbitmq',
  'other_deviance-standard_sgcp-secrets',
  'other_deviance-standard_sgcp-showback',
  'other_deviance-standard_sgcp-slb',
  'other_deviance-standard_sgcp-sql-server-suite',
  'other_deviance-standard_sgcp-terraform-provider',
  'other_deviance-standard_sgcp-traffic-manager',
  'other_deviance-standard_sgcp-vcs-virtual-server',
  'other_deviance-standard_sgdashboard',
  'other_deviance-standard_sgiam',
  'other_deviance-standard_sgmonitoring',
  'other_deviance-standard_sgshare-ms-wss-windows-sharepoint-services',
  'other_deviance-standard_sgsignin',
  'other_deviance-standard_sguid',
  'other_deviance-standard_sgwt',
  'other_deviance-standard_sgwt-connect',
  'other_deviance-standard_sgwt-structural-widgets',
  'other_deviance-standard_shell',
  'other_deviance-standard_simple-nlg',
  'other_deviance-standard_sinequa',
  'other_deviance-standard_sipo',
  'other_deviance-standard_skyhigh-security',
  'other_deviance-standard_skype-for-business',
  'other_deviance-standard_slf4j',
  'other_deviance-standard_smarsh',
  'other_deviance-standard_smax',
  'other_deviance-standard_sn1per-professional',
  'other_deviance-standard_snowflake',
  'other_deviance-standard_snowflake-on-aws',
  'other_deviance-standard_snowflake-on-azure',
  'other_deviance-standard_sonar',
  'other_deviance-standard_sophos',
  'other_deviance-standard_spark',
  'other_deviance-standard_specflow',
  'other_deviance-standard_spectrum-symphony',
  'other_deviance-standard_spitab',
  'other_deviance-standard_splunk',
  'other_deviance-standard_spring-boot',
  'other_deviance-standard_spring-cloud-gateway',
  'other_deviance-standard_spring-framework-suite',
  'other_deviance-standard_spring-native',
  'other_deviance-standard_springfox',
  'other_deviance-standard_sql-server',
  'other_deviance-standard_sql-server-database-mirroring',
  'other_deviance-standard_sqlbrowser',
  'other_deviance-standard_sqlite',
  'other_deviance-standard_sterling-connectdirect',
  'other_deviance-standard_sterling-connectexpress-tom',
  'other_deviance-standard_sterling-connectexpress-tom-zos',
  'other_deviance-standard_sterling-transformation-extender',
  'other_deviance-standard_storybook',
  'other_deviance-standard_strategy',
  'other_deviance-standard_strobe-istrobe',
  'other_deviance-standard_subversion',
  'other_deviance-standard_suite-millenium',
  'other_deviance-standard_swashbuckle',
  'other_deviance-standard_symantec',
  'other_deviance-standard_symantec-dlp',
  'other_deviance-standard_symantec-endpoint',
  'other_deviance-standard_system-center-configuration-manager-sccm',
  'other_deviance-standard_tableau-software',
  'other_deviance-standard_talend-esb',
  'other_deviance-standard_talend-esb-candidate',
  'other_deviance-standard_talend-etl',
  'other_deviance-standard_task-aggregator',
  'other_deviance-standard_team-foundation-server-tfs',
  'other_deviance-standard_teamcity',
  'other_deviance-standard_teams',
  'other_deviance-standard_temurin',
  'other_deviance-standard_teradata-datawarehouse-appliance',
  'other_deviance-standard_teradata-datawarehouse-appliance-gamme-27xx',
  'other_deviance-standard_terraform',
  'other_deviance-standard_thehive-project',
  'other_deviance-standard_think-cell',
  'other_deviance-standard_tibco-activematrix-businessworks',
  'other_deviance-standard_tibco-activematrix-service-bus',
  'other_deviance-standard_tibco-ems',
  'other_deviance-standard_tibco-iprocess-suite',
  'other_deviance-standard_tibco-rendezvous',
  'other_deviance-standard_tivoli-asset-discovery-for-zos-tadz',
  'other_deviance-standard_tivoli-storage-manager',
  'other_deviance-standard_tivoli-system-automation',
  'other_deviance-standard_tmon-solution',
  'other_deviance-standard_tools-db2',
  'other_deviance-standard_tools-ims-tm',
  'other_deviance-standard_top-secret',
  'other_deviance-standard_topaz-connect',
  'other_deviance-standard_topaz-workbench',
  'other_deviance-standard_traffic-manager',
  'other_deviance-standard_trift',
  'other_deviance-standard_trino',
  'other_deviance-standard_trino-before-presto',
  'other_deviance-standard_trivy',
  'other_deviance-standard_trustbuilder',
  'other_deviance-standard_tufin',
  'other_deviance-standard_tyk',
  'other_deviance-standard_typescript',
  'other_deviance-standard_uc4',
  'other_deviance-standard_uct-user-certification-tool',
  'other_deviance-standard_uipath',
  'other_deviance-standard_unified-certification-tool-duar',
  'other_deviance-standard_unified-certification-tool-tar',
  'other_deviance-standard_unipass-ejbca',
  'other_deviance-standard_unipass-pki',
  'other_deviance-standard_universal-query-engine',
  'other_deviance-standard_varonis',
  'other_deviance-standard_vault',
  'other_deviance-standard_verba',
  'other_deviance-standard_veritas-enterprise-vault',
  'other_deviance-standard_virtualbox',
  'other_deviance-standard_vitejs',
  'other_deviance-standard_vmware-vcloud-suite-enterprise',
  'other_deviance-standard_voir',
  'other_deviance-standard_vscode',
  'other_deviance-standard_vue-js',
  'other_deviance-standard_webgateway',
  'other_deviance-standard_weblogic-server',
  'other_deviance-standard_website-v2-liferay',
  'other_deviance-standard_websphere-application-server-for-zos',
  'other_deviance-standard_whats',
  'other_deviance-standard_windows-defender',
  'other_deviance-standard_windows-firewall',
  'other_deviance-standard_workfusion',
  'other_deviance-standard_workspace-one-boxer',
  'other_deviance-standard_wso2-api-management',
  'other_deviance-standard_xped',
  'other_deviance-standard_xunit',
  'other_deviance-standard_yammer',
  'other_deviance-standard_zabbix',
  'other_deviance-standard_zip390',
  'other_deviance-standard_zowe',
] as const;

export const COST_RESULT_KEYS = [
  'tco_gbsu-cost_workforce',
  'tco_gbsu-cost_workforce-prev-year',
  'tco_gbsu-cost_workforce-forecast',
  'tco_gbsu-cost_non-workforce',
  'tco_gbsu-cost_non-workforce-forecast',
  'tco_gbsu-cost_non-workforce-prev-year',
  'tco_vendor-cost_software',
  'tco_vendor-cost_software-forecast',
  'tco_vendor-cost_software-prev-year',
  'tco_infrastructure-cost_gts',
  'tco_infrastructure-cost_gts-forecast',
  'tco_infrastructure-cost_gts-prev-year',
  'tco_infrastructure-cost_azure',
  'tco_infrastructure-cost_azure-forecast',
  'tco_infrastructure-cost_azure-prev-year',
  'tco_infrastructure-cost_aws',
  'tco_infrastructure-cost_aws-forecast',
  'tco_infrastructure-cost_aws-prev-year',
  'cost',
  'cost-prev-year',
  'cost-forecast',
] as const;

export const OBSOLESCENCE_PERCENTAGE_KEY = 'obsolescence-percentage';
export const OBSOLESCENCE_ASSET_SUM_KEY = 'obsolescence-assets-sum';

export const RESULT_KEYS = [
  ...COMPONENT_RESULT_KEYS,
  ...SCOPE_RESULT_KEYS,
  ...PARENT_RESULT_KEYS,
  OBSOLESCENCE_PERCENTAGE_KEY,
  OBSOLESCENCE_ASSET_SUM_KEY,
] as const;

export type ComponentResultKey = (typeof COMPONENT_RESULT_KEYS)[number];
export type ScopeResultKey = (typeof SCOPE_RESULT_KEYS)[number];
export type ParentResultKey = (typeof PARENT_RESULT_KEYS)[number];
export type ResultKey = (typeof RESULT_KEYS)[number];
export type CostResultKey = (typeof COST_RESULT_KEYS)[number];

export type IApplicationFlat = {
  applicationId: number;
  iAppliID: string;
  iAppliName: string;
  recordDate: Date;
  createdDate: Date;
  description: string;
} & { [key in ComponentResultKey | ScopeResultKey]: string };

export interface INotifications {
  applicationId: number;
  roadmapsOutdated: number;
  claimsRequested: number;
  recommendation: number;
}

export type NotificationType = 'claims' | 'roadmaps' | 'recommendation' | 'exception' | 'roadmapsOutdated';

export type IInformationFormat = {
  key: string;
  children: IInformationFormat[];
};

export interface ICertified {
  applicationId: string;
  isCertified: boolean;
}

export enum ResultStatus {
  CERTIFIED = 'certified',
  ERROR = 'error',
  IDLE = 'idle',
}

export type MetassetSonar = {
  bugs: {
    count: number;
    rating: string;
  };
  vulnerabilities: {
    count: number;
    rating: string;
  };
  hotspotsReviewed: {
    count: number;
    rating: string;
  };
  codeSmell: {
    count: number;
    rating: string;
  };
  coverage: {
    percentage: number;
    lineCoverage: number;
  };
  duplications: {
    duplicatedLines: number;
    duplicatedLinesDensity: number;
  };
  ncloc: number;
  languageList: Array<string>;
  lastAnalysisDate: string;
  assetUrl: string;
  assetId: string;
};

export type TrafficManagerMember = {
  ip: string;
  name: string;
  priority: number;
  region: string;
  availabilityZone: string;
  uuid: string;
  enabled: boolean;
};

export interface IMetadata {
  previousValueCmp?: 'decrease' | 'equal' | 'increase' | null;
  ruleVersion?: string;
  ruleLink?: string;
  ruleKey?: string;
  name?: string;
  isCost?: boolean;
  importMonth?: string;
  isDuplicatedApptioResult?: boolean;
  activeSupport?: string;
  activeSupportSource?: string;
  activeSupportLastUpdateBy?: string;
  activeSupportLastUpdateDate?: string;
  securitySupport?: string;
  securitySupportSource?: string;
  securitySupportLastUpdateBy?: string;
  securitySupportLastUpdateDate?: string;
  extendedSupport?: string;
  extendedSupportSource?: string;
  extendedSupportLastUpdateBy?: string;
  extendedSupportLastUpdateDate?: string;
  lifeCycleManagement?: string;
  lifeCycleManagementSource?: string;
  lifeCycleManagementLastUpdateBy?: string;
  lifeCycleManagementLastUpdateDate?: string;
  eolScore?: string;
  link?: string;
  host?: string; // hostname of the dali asset
  zone?: string; // zone of the dali asset
  uid?: string; // uid of the dali asset
  serverVersion?: string; // os version for transformation path assets
  serverOs?: string; // os name for transformation path assets
  members?: Array<TrafficManagerMember>;
  obsolescence?: {
    totalInstanceCount: number;
    obsoleteInstanceCount: number;
    obsolescenceDenominator: number;
    percentageObsolete: number;
  };
  persistentResult?: PersistentResultMetata;
  serverCloudType?: string;
  daliGoldenSource?: string;
  isActive?: boolean;
  monitoring?: Record<string, unknown>[] | null;
  webAppUrls?: string;
  /**
   * Extra information about a result
   */
  sonar?: MetassetSonar;
  data?: {
    [key: string]: string;
  };
  dataArray?: {
    [key: string]: Array<Record<string, string>>;
  };
  transformationAction?: NorthStarTarget;
  transformationDate?: string;
  transformationStartDate?: string;
  transformationEndDate?: string;
  transformationProgress?: NorthStarTransfoProgress;
  austinStartDate?: string;
  austinEndDate?: string;
  northStarTarget?: NorthStarTarget;
  itResourceSubTower?: string;
  itResourceTower?: string;
  cve?: string; // JFrog SCA
  fixedVersion?: string; // JFrog SCA
  impactPath?: string; // JFrog SCA
  path?: string; // JFrog SCA
  saving?: string | number;
}

export enum PersistentResultType {
  ASSET = 'asset',
  VULNERABILITY = 'vulnerability',
  FINOPS = 'finops',
}

export type PersistentResultMetata =
  | ({
      assetHostname: string;
      assetLink: string;
      assetName: string;
      assetServerCloudType: string;
      assetUid: string;
      assetZone: string;
    } & {
      type: PersistentResultType.ASSET;
    })
  | ({
      vulnerabilityCve: string;
      vulnerabilityDeadline: string;
      vulnerabilityDescription: string;
      vulnerabilityFixedVersion: string;
      vulnerabilityImpactedArtifact: string;
      vulnerabilityImpactPath: string;
      vulnerabilityPath: string;
      vulnerabilityProjectKey: string;
      vulnerabilityVulnerableComponent: string;
    } & {
      type: PersistentResultType.VULNERABILITY;
    })
  | ({ resourceLink: string; resourceName: string } & {
      type: PersistentResultType.FINOPS;
    });

export interface IVersion {
  id: number;
  version: string;
  status: string;
  description: string | null;
}

export type IdType = 'iAppliID' | 'kearID';

export interface IApplicationFullResult {
  createdDate: Date;
  applicationId: number;
  status: ResultStatus;
  key: string;
  type: ApplicationResultType;
  resultId: number;
  name: string;
  parentRefId: number | null;
  scopeRefId: number | null;
  componentRefId: number | null;
  parentId: number | null;
  scopeId: number | null;
  componentId: number | null;
  valueMutated: string;
  commentMutated: string;
  sourceIds: number[];
  sources: Array<{ name: string; link: string | null; comment: string | null }>;
  sourceIdsMutated: number[];
  recommendationIds: number[];
  confidenceId: number;
  confidenceMutated: {
    value: number;
    comment: string;
  };
  versionRefId: number | null;
  version: Pick<IApplicationVersionRef, 'description' | 'id' | 'status' | 'version'>;
  isMainResult: boolean;
  value: string;
  comment: string;
  good?: string;
  medium?: string;
  bad?: string;
  metadata: IMetadata | null;
  rule: ScopeResult;
}

export type IApplicationFull = {
  applicationId: number;
  global_taxon_iappliid: IApplicationFullResult[];
  global_taxon_iappliname: IApplicationFullResult[];
  global_taxon_kearid: IApplicationFullResult[];
  recordDate: string;
  global_taxon_description: IApplicationFullResult[];
  ownerIds: number[];
  tagIds: number[];
  claimIds: number[];
  exceptionIds: number[];
  recommendationIdsRecorded: number[];
  roadmapIdsRecorded: number[];
  roadmapIds: number[];
} & { [key in ResultKey]: IApplicationFullResult[] } & {
  [key in keyof IBaseEntity]: IBaseEntity[key];
};

export interface IApplicationsFullPayload {
  items: IApplicationFull[];
  meta: IPagination;
  links: {
    first: string;
    previous: string;
    next: string;
    last: string;
  };
}

export interface IHeader {
  resultKey: ResultKey | string;
  label: string | null;
  children?: IHeader[];
}
export interface IApplicationsFullResult {
  recordDate: Date;
  applicationId: number;
  [index: number]: IApplicationFullResult;
}

export type PreviousValueCmpType = 'equal' | 'increase' | 'decrease';

export interface IGroupedApplications {
  applicationName: string;
  results: IApplicationFull[];
}

export interface IGroupedApplicationsPaginate {
  applicationsFull: IApplicationFull[];
  totalPages: number;
  currentPage: number;
  totalItems: number;
}

export interface IUniqueVisitors {
  linePoints: IUniqueVisitorsInformation[];
  timeBucketApplied: string;
}

export interface IUniqueVisitorsInformation {
  uniqueVisitorsNumber: number;
  intervalStartingDate: Date;
}

export interface IEntityAggrByDate {
  date: string;
  count: number;
}

export interface ICountEvolution {
  label: string;
  results: IEntityAggrByDate[];
}
export interface IKpiKeys {
  applicationsCount: number;
  favoritesCount: number;
  recommendationsCount: number;
  exceptionsCount: number;
  roadmapsCount: number;
  certificationsCount: number;
  errorsCount: number;
}
export interface ICountKpi {
  date: string;
  counts: IKpiKeys;
}

export interface IGroupedTotalCostResult {
  groupedType: string;
  children: string[];
}

export interface ITotalCostResults {
  results: Partial<IApplicationResult>[];
  label: string;
}
export interface IHighchartsEvolution {
  date: string;
  value: number | null;
}
export interface IHighchartData {
  name: string;
  key?: string;
  data: IHighchartsEvolution[];
  stack: string;
  color: string;
}

export type ITotalRecord = {
  recordDate: string;
} & { [key in CostResultKey]: string };

export interface IMenuItem<T> {
  label: string;
  view: T;
  display: boolean;
  children?: IMenuItem<T>[];
}
export type SettingMenu = Array<IMenuItem<SETTINGS_VIEW>>;
export type DashboardMenu = Array<IMenuItem<DASHBOARD_VIEW>>;

export interface IChasing extends IBaseEntity {
  applicationId: number;
  chasingRefId: number | null;
  roadmapId: number | null;
  componentStatusId: number | null;
  resultId: number | null;
  action: string;
  result: IApplicationResult | null;
  roadmap: IRoadmap;
  chasingRef?: IChasingRef;
  recommendation: IRecommendationNext;
}

export interface IChasingRef extends IBaseEntity {
  recordFrom: string;
  recordTo: string;
  resultKey: string | null;
  type: string;
  targetedResultValue: string | null;
  targetedMode: string | null;
  chasings: IChasing[];
  roadmaps: IRoadmap[];
  recommendations: IRecommendationNext[];
  applicationId: null | number;
}

export type ChasingType = 'component-status' | 'roadmap' | 'result' | 'recommendation' | 'application';

export interface IActionPayload {
  action: 'send-mail' | 'none';
  mailBody: string;
  applicationIds?: number[];
}
export interface IApplicationsLanguages {
  name: string;
  percentage: number;
  repositories?: Array<{ name: string; link: string }>;
}

export type ScopeResult = 'good' | 'medium' | 'bad' | 'n/a' | 'n/d' | 'intermediate';

export interface IRule extends IBaseEntity {
  scopeKey: string;
  version: string;
  description: string;
  good: string | null;
  medium: string | null;
  bad: string | null;
}

export interface IHistory extends IBaseEntity {
  event: string;
  additionalInfo: string;
}

export type HistoryPaginated = Paginated<IHistory>;
export type ChasingRefPaginated = Paginated<IChasingRef>;
export type ResultPaginated = Paginated<IApplicationResult>;

export type HistoryEntity = string;
export type HistoryAction = 'creation' | 'deletion' | 'addition' | 'update';

export type HistoryEvent = `${HistoryEntity}.${HistoryAction}`;

export interface IRoadmapAncestors extends IRoadmap {
  parentRef?: IApplicationParentRef;
  scopeRef?: IApplicationScopeRef[];
}

export interface IRecordResult {
  good: number | null;
  bad: number | null;
  medium: number | null;
  noData: number | null;
  notApplicable: number | null;
  value: number | null;
  recordDate: string;
}

export interface IRecordResultsByKey {
  resultKey: string;
  results: IRecordResult[];
}

export interface ITotalCount {
  total: number;
}

export type ReducerActionMap<M extends Record<string, unknown>> = {
  [Key in keyof M]: M[Key] extends undefined
    ? {
        type: Key;
      }
    : {
        type: Key;
        payload: M[Key];
      };
};

export type ComponentRefFromApi = {
  componentRefId: number;
  componentRefName: string;
  componentRefType: string | null;
  scopeRefId: number;
  scopeRefName: string;
};

export interface RecommandationWithRemediation {
  applicationId: number;
  applicationName: string;
  applicationTribe: string;
  applicationCriticality: string;
  applicationStatus: string;
  parentRefId: number;
  scopeRefId: number;
  scopeRefName: number;
  componentRefId: number | null;
  componentRefKey: string;
  componentRefName: string;
  componentRefType: string;
  versionRefId: number;
  version: string;
  recommendationId: string;
  recommendationCreatedDate: string;
  isManagedByExtendedTransactis: boolean;
  recommendationTitle: string;
  recommendationComment: string;
  recommendationDocumentationLink: string | null;
  recommendationActualValue: string | null;
  recommendationExpectedValue: string | null;
  recommendationStatus: RecommendationStatus;
  recommendationCriticity: string;
  recommendationType: string;
  roadmapExpectedDate: string | null;
  recommendationDueDate: string | null;
  roadmapId: number | null;
  roadmapComment: string | null;
  roadmapStatus: string | null;
  roadmapType: string | null;
  roadmapAssets: PersistentResultDTO[] | null;
  roadmapAssetNames: Array<string>;
  roadmapAssetZones: Array<string>;
  exceptionComment: string | null;
  reportingSort: string | null;
  exceptionRafId: string | null;
  exceptionAssets: string | null;
  exceptionAssetNames: string | null;
  exceptionAssetZones: string | null;
  resultType: string | null;
  noMainResultValues: { name: string | null; value: string | null }[] | null;
  kearID: string;
  ownersEmail: string[];
  applicationRegulations: string[];
}

export interface Inventory {
  id: string;
  lastRefreshDate: Date;
  applicationId: number;
  resultId: number;
  resultValue: string;
  applicationTribe: string | null;
  applicationTribeLevel1: string | null;
  applicationTribeLevel2: string | null;
  applicationTribeLevel3: string | null;
  iAppliID: string | null;
  kearID: string | null;
  iAppliName: string | null;
  ownersEmail: Array<string> | null;
  applicationTrigram: string | null;
  applicationCriticality: string | null;
  applicationType: string | null;
  applicationCountry: string | null;
  applicationIsAmerManaged: string | null;
  applicationManagedBy: string | null;
  applicationOwnedBy: string | null;
  applicationStatus: string | null;
  applicationOwnershipRc: string | null;
  componentRefId: number | null;
  componentRefKey: string | null;
  componentRefType: string | null;
  componentRefName: string | null;
  scopeRefName: string | null;
  versionRefId: number | null;
  version: string | null;
  advisedVersion: string | null;
  assetServerCloudType: string | null;
  assetNumberOfCores: string | null;
  assetInstalledMemory: string | null;
  assetNumberOfLogicalCpu: string | null;
  assetHostname: string | null;
  assetZone: string | null;
  assetName: string | null;
  assetArchitectureType: string | null;
  assetUid: string | null;
  assetServerStatus: string | null;
  assetServerEnvironment: string | null;
  assetServerBuilding: string | null;
  assetServerRoom: string | null;
  assetServerSite: string | null;
  assetServerCountry: string | null;
  assetServerExposed: string | null;
  assetServerServiceOffer: string | null;
  assetServerMainIpAddress: string | null;
  assetServerVrfName: string | null;
  assetServerBrand: string | null;
  eolSupport: string | null;
  eolSupportDateSource: string | null;
  eolSupportDateLastUpdateBy: string | null;
  eolSupportDateLastUpdateDate: Date | null;
  eolActive: string | null;
  eolSecurity: string | null;
  eolExtended: string | null;
  lifeCycleManagement: string | null;
  exceptionComment: string | null;
  roadmapId: number | null;
  roadmapId2: number | null;
  roadmapExpectedDate: Date | null;
  roadmapExpectedDate2: Date | null;
  roadmapComment: string | null;
  roadmapComment2: string | null;
  roadmapStartDate: Date | null;
  roadmapStartDate2: Date | null;
  roadmapActions: Pick<RoadmapAction, 'comment' | 'date' | 'type'>[] | null;
  itrmObsolescenceStatus: string;
  recommendationId: number | null;
  recommendationTitle: string;
  recommendationStatus: string | null;
  recommendationType: string | null;
  recommendationId2: number | null;
  recommendationTitle2: string;
  recommendationStatus2: string | null;
  recommendationType2: string | null;
  isManagedByExtendedTransactis: boolean | null;
  isCBS: boolean;
  isOutOfScope: boolean;
  eolYear: string | null;
  isManagedByGTS: boolean;
  eolDate: string | null;
  roadmapExpectedDateWithoutTimeZone: string | null;
  roadmapExpectedDateWithoutTimeZone2: string | null;
  assetTransformationPath: string | null;
  assetTransformationTarget: string | null;
  assetTransformationDate: Date | null;
  applicationTransformationPath: string | null;
  applicationTransformationTarget: string | null;
  applicationTransformationDate: string | null;
  applicationAustinStartDate: string | null;
  applicationAustinEndDate: string | null;
  assetServerVlanId: string | null;
  assetServerSubnetName: string | null;
  applicationRegulations: string[] | null;
}

export type GroupedFilters = Array<Record<string, string | null>>;
export type FilterPossibleValues = GroupedFilters | string[];

export interface RecommandationWithRemediationFiltersValues {
  filters: Record<keyof RecommandationWithRemediation | 'scopes', FilterPossibleValues>;
}

export interface InventoryFiltersValues {
  filters: Record<keyof Inventory, FilterPossibleValues>;
}

export enum WebAppEnvironment {
  PRODUCTION = 'production',
  DEVELOPMENT = 'development',
}

export interface DrawGraphNodeData {
  id?: string;
  label?: string;
  color?: string;
  source?: string;
  target?: string;
  arrow?: string;
  type?: string;
  key?: string;
  countLink?: number;
  links?: string;
  level?: number;
}

export interface WebAppNode {
  data: DrawGraphNodeData;
  position?: string;
}

export * from './bookmark.type';
export * from './communications.type';
export * from './excel.type';
export * from './exception.type';
export * from './filter.type';
export * from './kpi.type';
export * from './my-scope.type';
export * from './nova.type';
export * from './obsolescence.type';
export * from './persistent-result.type';
export * from './recommendation-next.type';
export * from './remediations.type';
export * from './roadmap.type';
export * from './thirdpartySoftware.type';
export * from './total.type';
export * from './user.type';
export * from './versionRef.type';
